/**
 * cart-coupon-hub.js
 * Mirrors misto-pos useCartCalculate / useCoupon / usePromotion patterns.
 * Policy SSOT: wiki/cart-checkout-hub.md §핵심 3원칙
 *   - PRODUCT 자동할인: 1라인당 최대 1개
 *   - ORDER 코드 쿠폰: 1주문당 최대 1개
 *   - 쿠폰 eligible 은 프로모션 적용 여부와 무관하게 전체 카트 라인 기준
 *
 * 🔴 모듈화 SSOT (2026-05-28) — wiki/docs/cart-promo-modularization-2026-05-28.md
 *   본 hub 는 Strangler Fig + Wrapper 1줄 패턴으로 7 모듈 분리됨 (2613 → 1775, 32% 감소).
 *   - 호출부 `this.X(...)` 무수정 룰 — wrapper 1줄로 본문만 cart-promo-*.js 이동.
 *   - module 함수 첫 인자로 hub instance 받아 `hub.state.X` 접근 (module 안 `this` 사용 금지).
 *   - module-level state 는 완전 캡슐화 (예: cart-promo-cascade.js 의 cartConfirm singleton).
 *   - module-level flag 는 무한 루프 차단 용도만 (예: cart-promo-revalidate.js 의 _revalidated).
 *   - 새 phase 진행 시 같은 패턴 강제 — 호출부 손대면 회귀 표면 폭증.
 *   분리 모듈: cart-coupon-utils / cart-promo-{effects,storefront,bxgy,cascade,sync,revalidate}.js
 *
 * 🔴 stale discount 자동 재계산 (2026-05-28) — wiki/docs/cart-stale-discount-revalidate-2026-05-28.md
 *   connectedCallback 끝에서 CartPromoRevalidate.revalidateCartDiscounts(this) 호출.
 *   Shopify cart discount_application snapshot 이 Admin 변경 후 stale 인 동작 우회.
 *   cascade-only 모드 (data-coupon-hub 없는 인스턴스) 는 skip.
 *
 * 🔴 Admin combinesWith 부정합 트랙 (2026-05-28) — wiki/docs/cart-promo-combineswith-policy-mismatch-2026-05-28.md
 *   7건 (SEASON DISCOUNT / VIP·VVIP Welcome Kit / Birthday VIP·VVIP / VIPKIT) combinesWith 전부 false.
 *   정책 §3 와 부정합 → FE 우회 금지, Admin 수정 결정 대기. 운영팀 협의 별도 트랙.
 */

(function () {
  'use strict';

  if (window.customElements && window.customElements.get('coupon-hub')) return;

  // 🔴 (2026-05-28) migrate 동시 실행 가드 — 중복 인스턴스 mount (refreshCartSection 직후 등) 시
  //   migrate 가 동시에 돌면 서로 라인을 re-key → stale key → /cart/change.js 400 race.
  //   module-level in-flight boolean 으로 concurrent 차단 (sequential 은 허용, 페이지 lifetime).
  let migrateInFlight = false;

  class CouponHub extends HTMLElement {
    constructor() {
      super();
      this.state = {
        promotions: {}, // { variantId: Promotion[] } or { productId: ... }
        coupons: [], // owned coupons (ORDER class)
        activeCouponId: null,
        // 🔴 inputAppliedCode (2026-05-24) — Have a code 로 적용했지만 dropdown 보유 쿠폰엔 없는 custom code.
        //    dropdown 보유 쿠폰은 dropdown 자체에 selected 표시 + 이 값은 null.
        //    custom code 일 때만 set → renderCouponSection 이 applied 카드 노출.
        inputAppliedCode: null,
        bxgyContext: null,
      };
      this.cartToken = this.dataset.cartToken;
      this.apiBase = this.dataset.apiBase || 'https://lkk-mhk-store-api.mistobrand.com';
      this.customerId = this.dataset.customerId || '';
      this.shopDomain = this.dataset.shopDomain || window.Shopify?.shop || '';
      // Storefront API token (public, unauthenticated). BXGY 모달 재고 정합 검증용.
      // 미설정 시 storefront 보정 skip → BE stock 단독 신뢰 (기존 동작).
      this.storefrontToken = this.dataset.storefrontToken || '';
      this.bxgyCartAddEnabled = this.dataset.bxgyCartAddEnabled !== 'false';
      this._storefrontProductCache = {};
      this._abortCtrl = null; // listener cleanup (refreshCartSection DOM 교체 시 누수 방어)
    }

    connectedCallback() {
      // refreshCartSection 의 innerHTML 교체로 새 element 가 생기면 — 이전 element 의
      // listener 가 document 에 leak. AbortController 로 disconnectedCallback 시 자동 제거.
      this._abortCtrl = new AbortController();
      this.bindDelegations();

      // 🔴 cascade-only 모드 (2026-05-27) — `data-coupon-hub` attr 없으면 데이터 fetch / renderAll skip.
      //    cart-drawer.liquid 가 마운트하는 인스턴스가 이 모드 — 부모 삭제 시 자식 BXGY 라인 cascade 만 필요.
      //    PLP/PDP 등 카트 페이지 외에서 매번 fetchCoupons / fetchAppliedDiscount 호출 부담 회피.
      //    cascade 는 document level capture-phase listener (bindDelegations) 라 attr 없이도 정상 동작.
      if (!this.hasAttribute('data-coupon-hub')) return;

      this.loadInitialData();
      // 🔴 stale discount 자동 재계산 (2026-05-28).
      //   Admin discount 삭제·만료 후 cart 가 stale 상태로 남는 Shopify 동작 우회.
      //   module-level flag 로 페이지 lifetime 1회만 실행. cart-stock-guard 와 같은 layer.
      window.CartPromoRevalidate?.revalidateCartDiscounts(this);
      document.addEventListener('cart:updated', () => this.revalidateAll(), {
        signal: this._abortCtrl.signal,
      });
    }

    disconnectedCallback() {
      // 모든 listener 일괄 cleanup — DOM 교체 시 여러 인스턴스 누적 방지.
      if (this._abortCtrl) {
        this._abortCtrl.abort();
        this._abortCtrl = null;
      }
    }

    bindDelegations() {
      // promo-cell 은 cart-items 안, coupon-hub 는 cart__summary 안 → DOM tree 가 분리됨.
      // change/click 모두 document 에서 잡고 selector 로 라우팅.
      // signal 로 disconnectedCallback 시 자동 제거 (listener leak 방어).
      const signal = this._abortCtrl?.signal;
      document.addEventListener('change', (e) => this.onChange(e), { signal });
      // 🔴 [중요] capture phase 등록 (2026-05-26) — 절대 bubble 로 바꾸지 말 것.
      // cart-remove-button (assets/cart.js) 가 element 자체에 click listener 박아서 target phase 에서 즉시 cart 라인 제거.
      // bubble phase 로 받으면 우리 handleParentRemoveCascade 의 preventDefault 가 too late.
      // capture 로 먼저 잡고 stopPropagation → cart-remove-button own listener 실행 차단.
      // 회귀 case: wiki/docs/bxgy-reentry-cleanup-and-cart-remove-button-capture-2026-05-26.md
      //   부모 X 클릭 → modal 뜨기 전 cart 라인 이미 제거 (modal Cancel 해도 부모 사라짐).
      document.addEventListener('click', (e) => this.onClickAny(e), { signal, capture: true });
    }

    onClickAny(e) {
      this.onClick(e);
      this.onDocumentClick(e);
    }

    onChange(e) {
      if (e.target.matches('[data-promo-select]')) {
        this.handlePromoChange(e.target);
      } else if (e.target.matches('[data-coupon-select]')) {
        this.handleCouponSelect(e.target);
      }
    }

    onClick(e) {
      if (e.target.closest('[data-cart-clear-all]')) return this.openCartConfirm({ action: 'clear-all' });
      // 🔴 stopPropagation (2026-05-27) — cart-drawer 내부에 modal 렌더 시 cart.js#handleOutsideClick (bubble) 가
      //    Remove/Cancel 클릭을 drawer outside 로 잘못 분류해 drawer 닫음. capture phase 에서 차단.
      //    카트 페이지에선 drawer 미관여라 무해.
      if (e.target.closest('[data-cart-confirm-ok]')) {
        e.stopPropagation();
        return this.handleCartConfirmOk();
      }
      if (e.target.closest('[data-cart-confirm-cancel]')) {
        e.stopPropagation();
        return this.closeCartConfirm();
      }
      if (e.target.closest('[data-apply-code]')) this.applyCode();
      if (e.target.closest('[data-remove-coupon]')) this.removeCoupon();
      // POS 패턴: 부모(anchor) cart-item 의 remove 버튼 클릭 → linked gift 까지 cascade confirm
      const removeBtn = e.target.closest('cart-remove-button');
      if (removeBtn) this.handleParentRemoveCascade(e, removeBtn);
    }

    onDocumentClick(e) {
      if (e.target.closest('[data-bxgy-confirm]')) {
        e.preventDefault();
        e.stopPropagation();
        void this.confirmBxgy();
      } else if (e.target.closest('[data-bxgy-cancel]')) this.cancelBxgy();
      else if (e.target.matches('[data-bxgy-overlay]')) this.cancelBxgy();
      else if (e.target.closest('[data-bxgy-qty]')) this.bxgyAdjustQuantity(e.target.closest('[data-bxgy-qty]'));
      else if (e.target.closest('[data-bxgy-load-more]')) this.bxgyLoadMore();
      else if (e.target.closest('[data-bxgy-pick]')) this.bxgyPick(e.target.closest('[data-bxgy-pick]'));
      else if (e.target.closest('[data-bxgy-deselect]')) this.bxgyDeselect(e.target.closest('[data-bxgy-deselect]'));
    }

    // ---- Data loading ----------------------------------------------------
    // misto-api 엔드포인트:
    //   - GET  {apiBase}/promotions?product_id=… (또는 variant_ids=…)
    //   - GET  {apiBase}/coupons/available?customer_id=…
    //   - POST {apiBase}/coupons/validation

    async loadInitialData() {
      // 🔴 [중요] migration 의 early return 제거 (2026-05-27). 회귀 fix.
      //   이전: `if (await migrate...) return` → cart 에 legacy `promotion_code` 키 있을 때마다
      //   migration 발동 → fetchPromotions / fetchCoupons / fetchAppliedDiscount 전부 skip
      //   → state.promotions={} → renderPromoCell 이 옵션 2개 (No promotion + applied fallback) 만 렌더 → 드롭다운 잠김 회귀.
      //   amount_off / BXGY 모두 setLinePromotion 이 legacy 키 박으므로 매 cart swap 마다 트리거됨.
      //   fix: migration 은 await 하되 결과 무시. fetch 작업은 항상 실행.
      //   회귀 case: docs/policy/cart-coupon-hub.md §변경 이력 (2026-05-27 Task #11).
      await this.migrateVisiblePromotionProperties();
      const variantIds = this.collectVariantIds();
      const tasks = [];
      if (variantIds.length > 0) tasks.push(this.fetchPromotions(variantIds));
      tasks.push(this.fetchCoupons());
      tasks.push(this.fetchAppliedDiscount());
      try {
        await Promise.all(tasks);
      } catch (err) {
        console.warn('[coupon-hub] initial load failed', err);
      }
      // 🔴 (2026-05-28) 카트 진입 시 깨진 BXGY 그룹 자동복구 (promo config 로드 후라 수량 불일치 검사 가능).
      //   구조/수량 불일치 그룹 → gift 제거 + anchor No promotion. 회귀 case: wiki/docs/bxgy-unique-line-identity-2026-05-28.md
      await window.CartPromoRevalidate?.cleanupInconsistentBxgyGroups(this);
      this.renderAll();
    }

    // 🔴 (2026-05-28) legacy visible(`promotion_*`) 키 정리 — 신규 라인은 promotionProperties 가 hidden 만
    //   박으므로 (visible 폐기) 이 migrate 는 *과거 배포 시점에 박힌 legacy 라인* 1회 정리용으로만 동작.
    //   회귀 방지 3중 안전망:
    //     1) module-level migrateInFlight 가드 — 중복 인스턴스 동시 실행 차단 (re-key race → 400 방지)
    //     2) per-line try/catch + ok 체크 — 한 라인 stale key (400) 가 전체 migrate abort 시키지 않음
    //     3) visible `promotion_*` 키 전부 제거 + hidden 보강 (promotion_code 만 아니라 전부)
    //   refreshCartSection 호출 안 함 — 다음 자연 user action 시 SSR 자연 갱신 (번쩍 회귀 회피).
    //   회귀 case: wiki/docs/bxgy-visible-promotion-keys-removal-2026-05-28.md
    async migrateVisiblePromotionProperties() {
      if (migrateInFlight) return false;
      migrateInFlight = true;
      try {
        const res = await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' });
        if (!res.ok) return false;
        const cart = await res.json();
        const hasVisiblePromoKey = (p) =>
          !!p && Object.keys(p).some((k) => k.indexOf('promotion_') === 0);
        const lines = (cart.items || []).filter((item) => hasVisiblePromoKey(item.properties));
        if (!lines.length) return false;

        for (const item of lines) {
          try {
            const next = { ...(item.properties || {}) };
            // visible `promotion_*` 키 전부 제거. hidden(`_promotion_*`) 누락 시 visible 값으로 보강 후 제거.
            for (const key of Object.keys(next)) {
              if (key.indexOf('promotion_') !== 0) continue; // hidden(`_promotion_`) 은 indexOf 0 아님
              const hiddenKey = `_${key}`;
              if (next[hiddenKey] === undefined) next[hiddenKey] = next[key];
              delete next[key];
            }
            // 🔴 quantity 명시 전달 (2026-05-27). properties-only body 는 Shopify 가 qty=1 로 reset (위키 박제).
            //   회귀 case: wiki/docs/cart-change-properties-only-qty-reset-trap.md
            const changeRes = await fetch(MISTO.localeUrl('/cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: next }),
            });
            // 한 라인 실패(stale key 등)는 skip — 전체 migrate abort 금지
            if (!changeRes.ok) {
              console.warn('[coupon-hub] migrate skip line', item.key, changeRes.status);
            }
          } catch (lineErr) {
            console.warn('[coupon-hub] migrate line err', item.key, lineErr);
          }
        }
        return true;
      } catch (err) {
        console.warn('[coupon-hub] migrateVisiblePromotionProperties', err);
        return false;
      } finally {
        migrateInFlight = false;
      }
    }

    // Shopify cart 의 적용된 discount code 조회 → state.activeCouponId 복원.
    // 단일 쿠폰 정책 — 첫 코드만 active 로.
    // 🔴 (2026-05-24) misto-api coupons (state.coupons) 에 없는 코드 = "input/external" 출처 →
    //    state.inputAppliedCode 에 기록. renderCouponSection 이 applied 카드로 노출.
    //    이전엔 state.coupons 에 임시 push 했으나, 새 UX (input-only 카드 분리) 와 충돌해서 분리.
    // 🔴 [중요] applicable:false 필터링은 정상 동작 — Shopify 가 거절한 코드를 적용된 척 표시 X.
    // "잠깐 보이다 사라짐" 회귀 보고 시 reject 사유는 cart.js 응답에 안 옴 → BE/admin 확인 영역.
    // 진단 패턴: ~/lukuku-dev-kei/projects/misto/wiki/docs/cart-coupon-shopify-applicable-false-diagnosis.md
    async fetchAppliedDiscount() {
      try {
        const res = await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' });
        if (!res.ok) return;
        const cart = await res.json();
        const codes = (cart.discount_codes || []).filter(d => d.applicable !== false);
        if (codes.length === 0) return;
        const code = codes[0].code;
        this.state.activeCouponId = code;
        // misto-api 보유 쿠폰에 없으면 input/external 코드 — 카드로 노출.
        if (!this.state.coupons.find((c) => c.id === code)) {
          this.state.inputAppliedCode = code;
        }
      } catch (err) {
        console.warn('[coupon-hub] fetchAppliedDiscount', err);
      }
    }

    collectVariantIds() {
      return Array.from(document.querySelectorAll('[data-cart-promo-cell]'))
        .map((c) => c.dataset.variantId)
        .filter(Boolean);
    }

    authHeaders() {
      // TODO: misto-api JWT 발급 미구현 (wiki cart-checkout-hub §회귀 함정)
      return { 'Content-Type': 'application/json' };
    }

    async fetchPromotions(variantIds) {
      const uniqueVariantIds = Array.from(new Set(variantIds.filter(Boolean).map(String)));
      if (uniqueVariantIds.length === 0) return;

      const cells = Array.from(document.querySelectorAll('[data-cart-promo-cell]')).filter((cell) =>
        uniqueVariantIds.includes(String(cell.dataset.variantId))
      );
      const byProduct = new Map();
      cells.forEach((cell) => {
        const productGid = cell.dataset.productGid || this.toShopifyGid('Product', cell.dataset.productId);
        if (!productGid) return;
        if (!byProduct.has(productGid)) byProduct.set(productGid, []);
        byProduct.get(productGid).push(cell);
      });

      this.state.promotions = {};
      cells.forEach((cell) => {
        this.state.promotions[cell.dataset.variantId] = [];
        if (cell.dataset.variantGid) this.state.promotions[cell.dataset.variantGid] = [];
        this.state.promotions[cell.dataset.productId] = [];
        if (cell.dataset.productGid) this.state.promotions[cell.dataset.productGid] = [];
      });

      let useVariantFallback = false;
      await Promise.all(Array.from(byProduct.entries()).map(async ([productGid, productCells]) => {
        try {
          const params = new URLSearchParams({ product_id: productGid });
          if (this.shopDomain) params.set('shop', this.shopDomain);
          const url = `${this.apiBase}/promotions?${params.toString()}`;
          const res = await fetch(url, { headers: this.authHeaders(), credentials: 'omit' });
          if (res.status === 404) {
            useVariantFallback = true;
            return;
          }
          if (!res.ok) throw new Error(`promotions ${res.status}`);
          const payload = await res.json();
          const promos = (payload.promotions || payload.data || [])
            .map((p) => this.normalizePromotion(p))
            .filter(Boolean);
          this.state.promotions[productGid] = promos;
          productCells.forEach((cell) => {
            this.state.promotions[cell.dataset.productId] = promos;
            this.state.promotions[cell.dataset.productGid || productGid] = promos;
            this.state.promotions[cell.dataset.variantId] = promos;
            if (cell.dataset.variantGid) this.state.promotions[cell.dataset.variantGid] = promos;
          });
        } catch (err) {
          console.warn('[coupon-hub] fetchPromotions', productGid, err);
        }
      }));

      if (useVariantFallback) {
        await this.fetchPromotionsByVariants(uniqueVariantIds);
      }

      console.info('[coupon-hub] promotions loaded', this.state.promotions);
    }

    async fetchPromotionsByVariants(variantIds) {
      const variantGids = variantIds.map((id) => this.toShopifyGid('ProductVariant', id));
      try {
        const params = new URLSearchParams({ variant_ids: variantGids.join(',') });
        if (this.shopDomain) params.set('shop', this.shopDomain);
        const url = `${this.apiBase}/v1/storefront/promotions?${params.toString()}`;
        const res = await fetch(url, { headers: this.authHeaders(), credentials: 'omit' });
        if (!res.ok) throw new Error(`promotions fallback ${res.status}`);
        const payload = await res.json();
        const byVariant = payload.data || payload.promotionsByVariant || payload.promotions || {};
        variantIds.forEach((variantId, idx) => {
          const gid = variantGids[idx];
          const promos = (byVariant[gid] || byVariant[variantId] || [])
            .map((p) => this.normalizePromotion(p))
            .filter(Boolean);
          this.state.promotions[variantId] = promos;
          this.state.promotions[gid] = promos;
        });
      } catch (err) {
        console.warn('[coupon-hub] fetchPromotionsByVariants', err);
      }
    }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js. wrapper 만 유지 — 호출부 (this.X) 변경 0.
    toShopifyGid(type, id) { return window.CartCouponHubUtils.toShopifyGid(type, id); }
    shopifyNumericId(id) { return window.CartCouponHubUtils.shopifyNumericId(id); }
    shopifyCartVariantId(id) { return window.CartCouponHubUtils.shopifyCartVariantId(id); }
    selectableVariantRawId(raw) { return window.CartCouponHubUtils.selectableVariantRawId(raw); }
    normalizePromotion(raw) { return window.CartCouponHubUtils.normalizePromotion(raw); }
    normalizeBxgySelectableProduct(raw, index = 0) { return window.CartCouponHubUtils.normalizeBxgySelectableProduct(raw, index); }
    productHandleFromUrl(url) { return window.CartCouponHubUtils.productHandleFromUrl(url); }

    // 🔴 [모듈] 본문은 assets/cart-promo-storefront.js (Phase 4a, 2026-05-27)
    //   instance state (cache, token, domain) 는 hub 에 유지 — wrapper 가 매개변수로 전달.
    resolveBxgyStorefrontProducts(items) {
      return window.CartPromoStorefront.resolveProducts(items, this._storefrontProductCache);
    }
    resolveBxgyStorefrontProduct(item) {
      return window.CartPromoStorefront.resolveProduct(item, this._storefrontProductCache);
    }
    fetchStorefrontProduct(handle) {
      return window.CartPromoStorefront.fetchProduct(handle, this._storefrontProductCache);
    }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js
    resolveVariantStock(variant) { return window.CartCouponHubUtils.resolveVariantStock(variant); }
    matchStorefrontVariant(item, variants) { return window.CartCouponHubUtils.matchStorefrontVariant(item, variants); }

    // 🔴 BXGY candidate 솔드아웃 FE 디펜시브 (2026-05-27).
    //   BE response 의 stock 부정합 (예약 hold 미반영 / cache stale) 으로 판매가능=0 상품이
    //   candidate 노출 → cart/add 422 회귀. /products/{handle}.js 의 variant.available 로 재검증.
    //   매칭 실패 / 404 (handle 불일치) → graceful fallback: BE stock 신뢰 (전체 후보 탈락 회피).
    //   handle 단위 grouping + _storefrontProductCache 활용. 인스턴스 단위 cache 이므로 두 번째 모달 열기엔 zero cost.
    // 🔴 [모듈] 본문은 assets/cart-promo-storefront.js (Phase 4a)
    verifyBxgyCandidatesAvailability(candidates) {
      return window.CartPromoStorefront.verifyAvailability(candidates, this._storefrontProductCache);
    }

    // 🔴 Storefront GraphQL 로 variant.quantityAvailable 받아 BE stock 보정 (2026-05-27).
    //   BE catalog `stock` 은 product_read_cache 기반 → stale / 과장 가능 (verified: 00L BE=4 vs 실제 1).
    //   Storefront API quantityAvailable 은 EC 채널 단독값 (HK_EC 만 EC 연결 정책) → admin HK_EC available 과 일치.
    //   token 미설정 시 skip (기존 BE stock 단독 신뢰).
    //   availableForSale=false → null 반환 (hide, /products/{handle}.js available 검증 백업).
    //   quantityAvailable=null (예: inventory_policy='continue') → BE stock 유지.
    //   회귀 case: docs/policy/cart-coupon-hub.md §변경 이력 (2026-05-27 storefront 정량 가드).
    // 🔴 [모듈] 본문은 assets/cart-promo-storefront.js (Phase 4a)
    fetchStorefrontInventory(variantGids) {
      return window.CartPromoStorefront.fetchInventory(variantGids, this.storefrontToken, this.shopDomain);
    }
    applyStorefrontStockCorrection(candidates) {
      return window.CartPromoStorefront.applyStockCorrection(candidates, this.storefrontToken, this.shopDomain);
    }

    async fetchCoupons() {
      // Phase 1 (current) — misto-storefront 무인증 endpoint, customer_id 만으로 조회.
      // 매장 분기 자동 (customer GID 자체가 HK/TW 별 unique sequence 라 격리).
      // POS 매장 (hk-001 등) 분기는 무관 (자사몰 전용 endpoint).
      //
      // ⚠️ 보안 약점 — customer_id 외부 노출 시 다른 사용자의 discount_code 조회 가능.
      //    Shopify customerSelection 으로 코드 사용 자체는 차단 (BDAY-* 같은 customer-
      //    specific 코드는 다른 customer reject) 되지만, 코드 정보 자체의 privacy
      //    위반 가능. customer GID 외부 노출 경로 통제 + 코드 민감도 평가 후 운영.
      //
      // TODO Phase 2 — Shopify App Proxy + HMAC 보안 강화:
      //   1. Partner Dashboard 의 양 app (HK Misto Customer Account / TW Misto
      //      Customer Account TW) 에 App Proxy 설정:
      //        - URL prefix: /apps
      //        - Subpath: coupons (또는 misto)
      //        - Proxy URL: https://mhk-stg-storefront-api.lukuku.co/storefront/proxy/coupons
      //   2. BE (misto-storefront) 에 신규 router (`/storefront/proxy/coupons/list`)
      //      — HMAC 검증 + shop → 매장별 app_secret 분기.
      //   3. 본 함수의 fetch URL → '/apps/coupons/list' — Shopify 가 자동 routing +
      //      logged_in_customer_id query param 주입 (외부 임의 호출 차단).
      //   4. authHeaders() — App Proxy 자체 인증 (별도 token X), 그대로 둠.
      //   5. K8s secret `misto-shopify-app-secret` 에 양 app secret 등록:
      //        SHOPIFY_HK_APP_SECRET / SHOPIFY_TW_APP_SECRET
      //
      // 작업 추정: BE 1-2일 + Partner Dashboard 설정 + Liquid 변경 30분.
      if (!this.customerId) {
        this.state.coupons = [];
        return;
      }
      try {
        const numericId = this.customerId.replace(/[^0-9]/g, '');
        const url = `${this.apiBase}/coupons/available?customer_id=${numericId}`;
        const res = await fetch(url, { headers: this.authHeaders() });
        if (!res.ok) throw new Error(`coupons ${res.status}`);
        const payload = await res.json();
        // BE 응답 → cart UI mock 정합 mapping.
        // 사용된 쿠폰 (is_used=true) 는 cart 표시 제외 — 사용 가능한 것만.
        const items = (payload.data || []).filter((c) => !c.is_used);
        this.state.coupons = items.map((c) => ({
          id: c.discount_code,
          name: c.title || c.discount_code,
          mode: c.discount_type === 'percentage' ? 'percentage' : 'fixed',
          value: Number(c.value) || 0,
          minAmount: Number(c.min_amount) || 0,
        }));
        console.info('[coupon-hub] coupons loaded', this.state.coupons.length);
      } catch (err) {
        console.warn('[coupon-hub] fetchCoupons', err);
        this.state.coupons = [];
      }
    }

    async validateCode(code) {
      if (!this.customerId) return null;
      try {
        const numericId = this.customerId.replace(/[^0-9]/g, '');
        const res = await fetch(`${this.apiBase}/coupons/validation`, {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({ code, memberId: numericId }),
        });
        if (!res.ok) return null;
        const data = await res.json();
        return data.coupon || null;
      } catch (err) {
        console.warn('[coupon-hub] validateCode', err);
        return null;
      }
    }

    // ---- Eligibility / state -------------------------------------------

    computeCouponEligibility() {
      // 🔴 (2026-09-14) 최소금액 판정 = 카트 소계 (`<coupon-hub data-cart-subtotal>`, cents).
      //   이전: [data-cart-promo-cell] 의 data-line-subtotal 합산 → 2026-06-10 promo-cell 임시 숨김 이후 셀 0개
      //   → eligibleSum 항상 0 → min 있는 쿠폰 전부 "Below min amount" + revalidateAll 이 적용 쿠폰 자동 해제.
      //   (line-subtotal 은 cents, minAmount 는 통화 단위라 셀을 복구해도 판정이 100배 느슨했음)
      //   quantity 변경·refreshCartSection 모두 cart-items 를 swap → hub 재생성이라 SSR 값이 항상 최신.
      const cells = Array.from(document.querySelectorAll('[data-cart-promo-cell]'));
      const eligibleLineKeys = cells.map((c) => c.dataset.variantId);
      const eligibleSum = (parseInt(this.dataset.cartSubtotal || '0', 10) || 0) / 100;
      const active = this.state.coupons.find((c) => c.id === this.state.activeCouponId);
      const belowMin = active ? eligibleSum < (active.minAmount || 0) : false;
      return { eligibleLineKeys, eligibleSum, belowMin };
    }

    getCartTotalQty() {
      return Array.from(document.querySelectorAll('[name="updates[]"]')).reduce(
        (s, i) => s + parseInt(i.value || '0', 10),
        0
      );
    }

    // ---- Render --------------------------------------------------------

    renderAll() {
      // renderAll 1회당 eligibility 1번만 계산 후 하위에 전달 (renderPromoCell N회 + renderCouponSection 의 N+1번 호출 방지)
      const eligibility = this.computeCouponEligibility();
      this.renderPromoCells(eligibility);
      this.renderCouponSection(eligibility);
      this.applyBxgyGiftMarking();
      this.clearOrphanBxgyGetPromotions();
    }

    // 🔴 [모듈] 본문은 assets/cart-promo-bxgy.js (Phase 4b)
    applyBxgyGiftMarking() { return window.CartPromoBxgy.applyGiftMarking(); }

    clearOrphanBxgyGetPromotions() {
      if (this._clearingOrphanBxgyGetPromotions) return;
      // 🔴 모달 열려있는 동안 skip (2026-05-26).
      // cleanup 의 removeCartLine → refreshCartSection 이 cart-items swap → hub 인스턴스 재생성
      // → 옛 hub 의 state.bxgyContext (모달 컨텍스트) 잃음 → 사용자 모달 조작 불가 회귀.
      // 모달 close 후 (confirmBxgy/cancelBxgy) 다음 renderAll 사이클에서 cleanup 처리되므로 안전.
      if (this.state?.bxgyContext) return;
      const cells = Array.from(document.querySelectorAll('#main-cart-items [data-cart-promo-cell]'));
      const hasBuyByPromo = new Map();
      cells.forEach((cell) => {
        if (cell.dataset.appliedPromotionType !== 'buy_x_get_y') return;
        const promoId = (cell.dataset.appliedPromotionId || '').trim();
        if (!promoId) return;
        const role = cell.dataset.appliedPromotionRole || '';
        if (role === 'buy' || cell.dataset.appliedPromotionAnchor === 'true') {
          hasBuyByPromo.set(promoId, true);
        }
      });

      const orphanGetCells = cells.filter((cell) => {
        if (cell.dataset.appliedPromotionType !== 'buy_x_get_y') return false;
        if (cell.dataset.appliedPromotionRole !== 'get') return false;
        const promoId = (cell.dataset.appliedPromotionId || '').trim();
        return promoId && !hasBuyByPromo.get(promoId);
      });
      if (!orphanGetCells.length) return;

      this._clearingOrphanBxgyGetPromotions = true;
      (async () => {
        try {
          for (const cell of orphanGetCells) {
            // 🔴 분기 (2026-05-26): catalog-added orphan 은 cart 라인 자체 제거.
            // 사용자가 원래 카트에 담은 라인을 GY 로 선택했던 케이스는 properties 만 clear (라인 보존).
            await this.clearOrRemoveLinePromotion(cell);
          }
          await this.refreshCartSection();
        } catch (err) {
          console.error('[coupon-hub] clear orphan bxgy get failed', err);
        } finally {
          this._clearingOrphanBxgyGetPromotions = false;
        }
      })();
    }

    findActivePromo(cell) {
      const id = (cell.dataset.appliedPromotionId || '').trim();
      if (!id) return null;
      const list =
        this.state.promotions[cell.dataset.variantId] ||
        this.state.promotions[cell.dataset.productId] ||
        [];
      return list.find((p) => p.id === id) || null;
    }

    getCartItemQty(item) {
      const input = item.querySelector('input[name="updates[]"]');
      if (input) return parseInt(input.value, 10) || 0;
      const staticSpan = item.querySelector('.cart-item__quantity-static');
      if (staticSpan) return parseInt(staticSpan.textContent, 10) || 0;
      return 0;
    }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (money/format/bxgy 라벨)
    parseMoney(text) { return window.CartCouponHubUtils.parseMoney(text); }
    formatMoney(n) { return window.CartCouponHubUtils.formatMoney(n); }
    formatBxgyPrice(value) { return window.CartCouponHubUtils.formatBxgyPrice(value); }
    bxgyGetDiscountLabel(promo) { return window.CartCouponHubUtils.bxgyGetDiscountLabel(promo); }
    bxgyBenefitMessage(promo, count) { return window.CartCouponHubUtils.bxgyBenefitMessage(promo, count); }
    bxgyGenerateGroupId() { return window.CartCouponHubUtils.bxgyGenerateGroupId(); }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (Phase M)
    refreshDropdownInput(select) { return window.CartCouponHubUtils.refreshDropdownInput(select); }

    renderPromoCells(eligibility) {
      document
        .querySelectorAll('[data-cart-promo-cell]')
        .forEach((cell) => this.renderPromoCell(cell, eligibility));
    }

    renderPromoCell(cell, eligibility) {
      // 자동할인 (Shopify auto discount) 이 이미 적용된 라인 — server-render 그대로 유지.
      // JS 가 select 를 덮어쓰지 않음. 쿠폰 read-only 라벨도 표시 X (자동할인 라인은 쿠폰 제외).
      if (cell.dataset.autoDiscount) {
        const tag = cell.querySelector('[data-coupon-tag]');
        if (tag) tag.classList.add('hidden');
        this.applyQtyLockState(cell);
        return;
      }

      const productId = cell.dataset.productId;
      const variantId = cell.dataset.variantId;
      const onSale = cell.dataset.onSale === 'true';
      const select = cell.querySelector('[data-promo-select]');
      const couponTag = cell.querySelector('[data-coupon-tag]');
      const couponName = cell.querySelector('[data-coupon-name]');
      if (!select) return;

      const promos = this.state.promotions[variantId] || this.state.promotions[productId] || [];
      const appliedId = cell.dataset.appliedPromotionId || '';
      const appliedRole = cell.dataset.appliedPromotionRole || '';
      const lockAppliedPromotion = !!appliedId && appliedRole === 'get';

      // 🔴 (2026-05-28) Sale item 차단 정책 폐기 — BE 가 할인가(sale price) 기준 1000 off 정상 동작.
      //   FE 차단은 catalog 자식엔 적용 안 돼서 일관성 깨짐. 정책 = sale item 도 BXGY 적용 가능.
      //   이전 회귀 박제: docs/policy/cart-coupon-hub.md §5 (정책 폐기 marker 추가 필요)
      if (promos.length === 0 && !appliedId) {
        select.innerHTML = '<option value="">No promotion available</option>';
        select.disabled = true;
        cell.classList.remove('is-promo-applied');
      } else {
        // 정책 (2026-05-07): 첫 옵션 = "No promotion" 일관 라벨.
        // 적용·해제 분기 라벨(× Remove / + Apply) 폐기 — applied 시각은 select 좌측 ✓ + border 로 충분.
        const opts = ['<option value="" ' + (appliedId ? '' : 'selected') + '>No promotion</option>'];
        const hasAppliedOption = promos.some((p) => p.id === appliedId);
        promos.forEach((p) => {
          const label =
            p.type === 'amount_off'
              ? `${p.name} (${p.mode === 'percentage' ? `-${p.value}%` : `-${this.formatMoney(p.value)}`})`
              : `🎁 ${p.name} (${p.buyQty}+${p.getQty})`;
          opts.push(`<option value="${p.id}" ${appliedId === p.id ? 'selected' : ''}>${label}</option>`);
        });
        if (appliedId && !hasAppliedOption) {
          const appliedLabel =
            cell.dataset.appliedPromotionTitle ||
            cell.dataset.appliedPromotionCode ||
            'Selected promotion';
          opts.push(
            `<option value="${this.escapeHtml(appliedId)}" selected>${this.escapeHtml(appliedLabel)}</option>`
          );
        }
        select.innerHTML = opts.join('');
        select.disabled = lockAppliedPromotion;
        cell.classList.toggle('is-promo-applied', !!appliedId);
      }
      this.refreshDropdownInput(select);

      // 🔴 (2026-05-28) BXGY 자격 미충족 인라인 메시지 — 적용된 promo 만 검사 (노이즈 방지).
      //   미적용 promo 의 자격 메시지는 dropdown 시도 시점에 toast 로 처리 (openBxgyModal 진입 가드).
      const bxgyErrorEl = cell.querySelector('[data-bxgy-error]');
      if (bxgyErrorEl) {
        let bxgyMessage = '';
        const appliedPromo = appliedId ? promos.find((p) => p.id === appliedId) : null;
        if (appliedPromo && appliedPromo.type === 'buy_x_get_y') {
          const eg = window.CartCouponHubUtils.checkBxgyEligibility(cell, appliedPromo);
          if (!eg.eligible) {
            if (eg.reason === 'insufficient') {
              bxgyMessage = `Buy ${eg.required} required (current ${eg.current})`;
            } else if (eg.reason === 'odd') {
              bxgyMessage = `Adjust quantity to multiple of ${eg.required} (current ${eg.current})`;
            }
          }
        }
        if (bxgyMessage) {
          bxgyErrorEl.textContent = bxgyMessage;
          bxgyErrorEl.hidden = false;
        } else {
          bxgyErrorEl.textContent = '';
          bxgyErrorEl.hidden = true;
        }
      }

      // Coupon read-only tag — eligibility 는 renderAll 이 1회 캐시 후 전달
      const active = this.state.coupons.find((c) => c.id === this.state.activeCouponId);
      const isEligibleForLine =
        active &&
        eligibility &&
        !eligibility.belowMin &&
        eligibility.eligibleLineKeys.includes(variantId);
      if (isEligibleForLine && couponTag && couponName) {
        couponName.textContent = active.name;
        couponTag.classList.remove('hidden');
      } else if (couponTag) {
        couponTag.classList.add('hidden');
      }

      this.applyQtyLockState(cell);
    }

    // 정책 (2026-05-07): 할인 적용된 라인 = qty 변경 차단.
    // POS 의 split 패턴은 모바일 POS 전용. 데스크탑 카트는 단순 disable + tooltip.
    // 변경하려면 promo 해제 후 가능. (autoDiscount 라인도 동일)
    // 🔴 [중요] split 패턴 다시 시도 금지. 회귀 case: wiki/docs/cart-promo-line-qty-policy.md
    applyQtyLockState(cell) {
      const cartItem = cell.closest('.cart-item');
      if (!cartItem) return;
      const locked = !!cell.dataset.autoDiscount || !!(cell.dataset.appliedPromotionId || '').trim();
      cartItem.classList.toggle('cart-item--promo-locked', locked);
      const tooltip = locked ? 'Remove promotion to change quantity' : '';
      cartItem.querySelectorAll('input[name="updates[]"]').forEach((inp) => {
        inp.disabled = locked;
        if (locked) inp.setAttribute('title', tooltip);
        else inp.removeAttribute('title');
      });
      cartItem.querySelectorAll('.cart-item__quantity .quantity__button').forEach((btn) => {
        btn.disabled = locked;
        if (locked) btn.setAttribute('title', tooltip);
        else btn.removeAttribute('title');
      });
    }

    renderCouponSection(eligibility) {
      const wrap = this.querySelector('[data-cart-coupon-section]');
      if (!wrap) return;
      const select = wrap.querySelector('[data-coupon-select]');
      const countEl = wrap.querySelector('[data-coupon-count]');
      const appliedWrap = wrap.querySelector('[data-applied-coupon]');
      const appliedNameEl = wrap.querySelector('[data-applied-coupon-name]');

      if (countEl) {
        countEl.textContent = this.state.coupons.length ? ` (${this.state.coupons.length})` : '';
      }

      if (select) {
        // 🔴 placeholder 라벨 = `No coupon` 항상 (2026-05-24) — promo cell `No promotion` 패턴과 일관.
        //    적용 카드 폐기 후 해제 진입점이지만 dropdown 아이템은 "선택" UI 이므로 액션 마커(×) 쓰지 않음.
        const opts = ['<option value="">No coupon</option>'];
        this.state.coupons.forEach((c) => {
          const meetsMin = eligibility.eligibleSum >= (c.minAmount || 0);
          const isActive = c.id === this.state.activeCouponId;
          const disabled = !isActive && !meetsMin;
          const reason = !meetsMin ? 'Below min amount' : '';
          // 🔴 (2026-09-14) 통화 기호 하드코딩 금지 — TW 는 TWD. formatMoney = shop.money_format SSOT.
          const valueDesc = c.mode === 'percentage' ? `${c.value}% off` : `${this.formatMoney(c.value)} off`;
          const label = `${c.name} — ${valueDesc} (Min ${this.formatMoney(c.minAmount)})${
            disabled ? ` · ${reason}` : ''
          }`;
          opts.push(
            `<option value="${c.id}" ${disabled ? 'disabled' : ''} ${
              isActive ? 'selected' : ''
            }>${label}</option>`
          );
        });
        select.innerHTML = opts.join('');
        this.refreshDropdownInput(select);
      }

      // 🔴 applied 카드 (2026-05-24) — Have a code 로 적용된 dropdown 외부 코드일 때만 노출.
      //    dropdown 보유 쿠폰은 dropdown 자체에 selected 표시 + state.inputAppliedCode=null 이라 카드 hidden.
      if (appliedWrap) {
        const inputCode = this.state.inputAppliedCode;
        if (inputCode) {
          if (appliedNameEl) appliedNameEl.textContent = inputCode;
          appliedWrap.classList.remove('hidden');
        } else {
          appliedWrap.classList.add('hidden');
        }
      }
    }

    // ---- Promotion change handler --------------------------------------

    async handlePromoChange(select) {
      const cell = select.closest('[data-cart-promo-cell]');
      const variantId = cell.dataset.variantId;
      const newPromoId = select.value;
      const promos =
        this.state.promotions[variantId] || this.state.promotions[cell.dataset.productId] || [];
      const newPromo = promos.find((p) => p.id === newPromoId);
      const prevAppliedId = cell.dataset.appliedPromotionId || '';

      // 🔴 BXGY 해제 정책 (2026-05-24) — confirm 팝업으로 사용자 의사 한 번 더 확인.
      // 처리 자체는 단순 properties clear (GY 라인 cart 보존, 정가 회복).
      // 팝업 사유: BXGY 해제는 cart 전체 가격 영향 크고, 처리 시간 좀 걸려서 사용자에게 진행 신호 필요.
      if (!newPromoId && prevAppliedId) {
        const prevPromo = promos.find((p) => p.id === prevAppliedId);
        if (prevPromo && prevPromo.type === 'buy_x_get_y') {
          this.openCartConfirm({
            action: 'remove-bxgy-promo',
            title: 'Remove promotion?',
            body: 'Remove this promotion from your cart?',
            okLabel: 'Remove',
            context: { cell, prevAppliedId, select, prevValue: prevAppliedId },
          });
          return;
        }
      }

      // 🔴 카트 영역 마스킹 (2026-05-27) — handlePromoChange 의 직접 cart-mutation 경로 (empty / amount_off) 에
      //   is-cart-loading 부착. /cart/update.js + refreshCartSection 처리 중 다른 라인 조작 race 차단 + 사용자 진행 신호.
      //   confirmBxgy 와 동일 패턴 (cart-items 영역만 마스킹, 헤더·summary 등 외부는 그대로).
      //   openBxgyModal / cart-confirm-modal 경로는 각자 마스킹 책임 (handlePromoChange 에선 마스킹 X).
      const cartItemsEl = document.querySelector('cart-items');
      const maskCart = () => cartItemsEl?.classList.add('is-cart-loading');
      const unmaskCart = () => requestAnimationFrame(() => {
        document.querySelector('cart-items')?.classList.remove('is-cart-loading');
      });

      if (!newPromoId) {
        const removeCells = this.promotionCellsToClear(cell, prevAppliedId);
        removeCells.forEach((targetCell) => {
          targetCell.dataset.appliedPromotionId = '';
          targetCell.dataset.appliedPromotionType = '';
          targetCell.dataset.appliedPromotionRole = '';
        });
        this.clearGiftRows(prevAppliedId);
        maskCart();
        try {
          await this.applyToCart({ removePromoCells: removeCells });
        } finally {
          unmaskCart();
        }
      } else if (newPromo && newPromo.type === 'amount_off') {
        // 🔴 BXGY → amount_off 전환 시 옛 자식 GY 라인 cleanup (2026-05-27).
        //   회귀: 이전엔 clearGiftRows (DOM 제거) + applyToCart(new promo only) → catalog-added 옛 GY 라인 cart 잔존.
        //   fix: promotionCellsToClear 로 옛 BXGY cells 수집 → anchor (= 이 cell) 제외 → applyToCart 의 removePromoCells 로 cleanup.
        //   non-BXGY → amount_off 케이스는 promotionCellsToClear 가 [sourceCell] 만 반환 → filter 로 빈 배열 → 기존 동작 동일 (no-op).
        //   clearGiftRows 호출 제거 — DOM-based cleanup 함정 회피 (#1 fix 의 SSOT 룰).
        //   회귀 case: wiki/docs/bxgy-reentry-cleanup-and-cart-remove-button-capture-2026-05-26.md
        const removeCells = this.promotionCellsToClear(cell, prevAppliedId).filter((c) => c !== cell);
        removeCells.forEach((targetCell) => {
          targetCell.dataset.appliedPromotionId = '';
          targetCell.dataset.appliedPromotionType = '';
          targetCell.dataset.appliedPromotionRole = '';
        });
        cell.dataset.appliedPromotionId = newPromoId;
        cell.dataset.appliedPromotionType = newPromo.type || '';
        maskCart();
        try {
          await this.applyToCart({ removePromoCells: removeCells, promoCell: cell, promo: newPromo });
        } finally {
          unmaskCart();
        }
      } else if (newPromo && newPromo.type === 'buy_x_get_y') {
        // 🔴 (2026-05-26) 여기서 clearGiftRows 호출하지 말 것. 이전 promo 의 GY cells 가 DOM 에서 사라지면
        // confirmBxgy 의 promotionCellsToClear(DOM 기반) 가 prev GY 라인을 못 잡아 cleanup 누락 회귀.
        // GY rows 정리는 confirmBxgy 내부에서 cleanup (line 1577) 또는 cancel 시 refreshCartSection 으로 복원.
        await this.openBxgyModal(cell, newPromo, prevAppliedId);
        return;
      }
      this.renderAll();
    }

    // ---- Coupon handlers ------------------------------------------------

    // 🔴 [중요] applicable 사후 검증 + 실패 시 inline msg + 미적용 롤백 + Shopify 측 un-apply (2026-05-24).
    //   `applyCode` 와 일관성 정합. cart.discount_codes[].applicable=false → state 롤백 + applyDiscountCode('').
    //   refreshCartSection 이 cart-items innerHTML swap → coupon-hub 인스턴스 재생성.
    //   inline msg 등 DOM 변경은 refresh 후 document.querySelector 로 NEW 인스턴스에 적용 필요.
    //   회귀 case: wiki/docs/cart-coupon-refresh-instance-reset-trap.md
    //   진단 패턴: wiki/docs/cart-coupon-shopify-applicable-false-diagnosis.md
    async handleCouponSelect(select) {
      const newId = select.value;
      if (newId === this.state.activeCouponId) return;

      const prevActiveId = this.state.activeCouponId;

      // 단일 쿠폰 enforce — 기존 적용 코드가 있으면 먼저 제거.
      if (prevActiveId) {
        await this.applyDiscountCode('');
      }

      // dropdown 진입은 보유 쿠폰 컨텍스트 — input-only 카드 flag clear.
      this.state.inputAppliedCode = null;

      let rejected = false;
      let rejectCouponName = '';
      if (newId === '') {
        this.state.activeCouponId = null;
      } else {
        this.state.activeCouponId = newId;
        const coupon = this.state.coupons.find((c) => c.id === newId);
        await this.applyDiscountCode(coupon.id);

        // 🔴 applicable 검증 (2026-05-24) — Shopify 가 reject 한 코드는 silently 사라지지 않게.
        let applied = false;
        try {
          const cart = await (await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' })).json();
          applied = (cart.discount_codes || []).some(
            (d) => (d.code || '').toUpperCase() === newId.toUpperCase() && d.applicable
          );
        } catch (err) {
          console.warn('[coupon-hub] cart.js after handleCouponSelect', err);
        }

        if (!applied) {
          this.state.activeCouponId = null;
          rejected = true;
          rejectCouponName = coupon ? coupon.name : newId;
          // 🔴 Shopify cart 에서도 실제 un-apply (silently 적용된 채 남는 회귀 차단).
          //    refreshCartSection 후 new coupon-hub 인스턴스가 fetchAppliedDiscount 에서
          //    이 코드를 active 로 잘못 복원하는 함정 방지.
          await this.applyDiscountCode('');
        }
      }

      // 🔴 refreshCartSection 이 cart-items 통째 swap → coupon-hub 새 인스턴스 + state 리셋.
      //    msg 는 refresh 후, NEW 인스턴스의 markup 을 document 에서 다시 찾아 적용.
      await this.refreshCartSection();
      this.renderAll();

      // 🔴 NEW coupon-hub 의 msg span 갱신 (this 는 detached old instance 일 가능성).
      const msgEl = document.querySelector('[data-cart-coupon-section] [data-coupon-msg]');
      if (msgEl) {
        msgEl.classList.remove('cart-coupon-section__msg--error', 'cart-coupon-section__msg--success');
        if (rejected) {
          msgEl.textContent = `Cannot apply ${rejectCouponName} — likely conflicts with active promotions, or below minimum`;
          msgEl.classList.remove('hidden');
          msgEl.classList.add('cart-coupon-section__msg--error');
        } else {
          msgEl.classList.add('hidden');
          msgEl.textContent = '';
        }
      }
    }

    async applyCode() {
      const input = this.querySelector('[data-code-input]');
      const msg = this.querySelector('[data-code-msg]');
      if (!input) return;
      const code = (input.value || '').trim().toUpperCase();
      if (!code) return;
      if (msg) {
        msg.classList.remove('hidden', 'cart-coupon-section__msg--error', 'cart-coupon-section__msg--success');
      }

      // 🔴 [중요] Have a code 입력은 Shopify 표준만 사용 (BE validation 의존 X).
      // 사유: misto-api JWT 인증 미구현 상태에서 /coupons/validation 가 항상 401 → 모든 코드 Invalid 처리되던 회귀 차단.
      // admin 등록 코드 (applicable / minimum / customerSelection / combinability) 검증은 Shopify 가 다 함.
      // dropdown / 보유 쿠폰 / 자동할인 / BXGY 흐름은 별개 — 손대지 않음.
      // 1주문 1코드 enforce 는 기존 정책 유지 (사전 clear).
      if (this.state.activeCouponId && this.state.activeCouponId !== code) {
        await this.applyDiscountCode('');
      }
      await this.applyDiscountCode(code);

      // Shopify cart.js 응답의 discount_codes[].applicable 로 적용 여부 판정.
      let applied = false;
      try {
        const cart = await (await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' })).json();
        applied = (cart.discount_codes || []).some(
          (d) => (d.code || '').toUpperCase() === code && d.applicable,
        );
      } catch (err) {
        console.warn('[coupon-hub] cart.js after applyCode', err);
      }

      if (!applied) {
        if (msg) {
          msg.textContent = 'Invalid code';
          msg.classList.remove('hidden');
          msg.classList.add('cart-coupon-section__msg--error');
        }
        return;
      }

      this.state.activeCouponId = code;
      // dropdown 보유 쿠폰에 같은 코드 있으면 dropdown 에 selected 로 표시되므로 카드 불필요.
      // custom code (외부 admin 등록) 일 때만 applied 카드 노출용 flag set.
      const inDropdown = this.state.coupons.some((c) => c.id === code);
      this.state.inputAppliedCode = inDropdown ? null : code;
      await this.refreshCartSection();
      if (msg) {
        msg.textContent = '✓ Applied';
        msg.classList.remove('hidden');
        msg.classList.add('cart-coupon-section__msg--success');
      }
      input.value = '';
      this.renderAll();
    }

    async removeCoupon() {
      this.state.activeCouponId = null;
      this.state.inputAppliedCode = null;
      await this.applyToCart({ removeCoupon: true });
      this.renderAll();
    }

    // ---- BXGY modal -----------------------------------------------------

    async openBxgyModal(cell, promotion, prevValue) {
      // 🔴 (2026-05-28) Y-only modal 진입 가드 — anchor qty 자격 검사.
      //   insufficient (qty < buyQty) / odd (qty % buyQty !== 0) → modal 안 열림 + 인라인 메시지 (data-bxgy-error) + toast + dropdown 복원.
      //   인라인 메시지가 toast 보다 시각적 인지 ↑ (cell 옆에 빨간 텍스트, 5초 자동 사라짐).
      const eligibility = window.CartCouponHubUtils.checkBxgyEligibility(cell, promotion);
      if (!eligibility.eligible) {
        let msg = '';
        if (eligibility.reason === 'insufficient') {
          msg = `Buy ${eligibility.required} required (current ${eligibility.current})`;
        } else if (eligibility.reason === 'odd') {
          msg = `Adjust quantity to multiple of ${eligibility.required} (current ${eligibility.current})`;
        }
        // 인라인 메시지 (cell 의 data-bxgy-error 영역) — 즉시 시각 피드백
        const errorEl = cell.querySelector('[data-bxgy-error]');
        if (errorEl && msg) {
          errorEl.textContent = msg;
          errorEl.hidden = false;
          // 5초 후 자동 사라짐. cart 변경 시 renderPromoCell 이 다시 갱신.
          clearTimeout(errorEl._bxgyTimer);
          errorEl._bxgyTimer = setTimeout(() => {
            errorEl.hidden = true;
            errorEl.textContent = '';
          }, 5000);
        }
        // toast 도 같이 (보조)
        if (msg) this.toast(msg);
        // dropdown 원래값 복원
        const select = cell.querySelector('select');
        if (select && prevValue !== undefined) select.value = prevValue;
        return;
      }
      const anchorSelectionId = this.bxgySelectionId(cell);
      this.state.bxgyContext = {
        lineKey: cell.dataset.lineKey,
        variantId: cell.dataset.variantId,
        productId: cell.dataset.productId,
        // 🔴 anchorCell ref (2026-05-24) — closeBxgyModal 의 dropdown 복원이 안전하게 cell 참조.
        // modal 열린 동안 cart DOM swap 거의 없음. fallback: lineKey query.
        anchorCell: cell,
        promotion,
        anchorSelectionId,
        // 🔴 (2026-05-28) Y-only modal — anchor 는 modal 밖 (상단 별도 행). selectedIds 는 빈 배열로 시작.
        // Modal 재진입 시에도 reset (기존 group 은 confirmBxgy 시점에 cleanup).
        existingGroupId: cell.dataset.appliedPromotionGroupId || '',
        // 🔴 (2026-05-28) reservedByVariant — modal 재진입 시 BE selectableProducts 의 stock 가 기존 BXGY 자식 qty 만큼 차감된 값.
        //    confirm 시 cleanup 되므로 stock 검사에서 add back 필요.
        reservedByVariant: {},
        selectedIds: [],
        selectedQuantity: 0,
        addQuantities: {},
        selectableProducts: [],
        // 🔴 grid 정합 — modal grid 는 데스크톱 3열 / 태블릿·모바일 2열. LCM(2,3)=6 의 배수여야 마지막 행이 깔끔.
        // 12 = 데스크톱 4행 / 태블릿 6행. 6 보다 한번에 더 보이게.
        catalogLimit: 12,
        // 🔴 renderTarget (2026-05-24) — 한 번에 보일 카드 수 cap. autoFill 로 더 fetch 됐어도 첫 진입엔 catalogLimit 만 노출.
        // Load more 클릭 시 += catalogLimit. catalogHasMore 와 별개로 hidden 있으면 Load more 활성.
        renderTarget: 12,
        catalogOffset: 0,
        catalogHasMore: false,
        catalogLoading: this.bxgyCartAddEnabled,
        catalogLoadingMore: false,
        catalogError: '',
        prevValue,
      };
      const required = promotion.buyQty + promotion.getQty;
      const cartQuantity = this.bxgyCartQuantity();
      this.renderBxgyModal();
      const modal = document.querySelector('[data-bxgy-modal]');
      if (modal) {
        modal.hidden = false;
        const scrollContainer = modal.querySelector('.cart-bxgy-modal__inner') || modal;
        if (typeof bodyScroll !== 'undefined') bodyScroll.lock(scrollContainer);
      }
      if (cartQuantity < required) {
        this.clearUnmetBxgyPromotion(cell).catch((err) => {
          console.warn('[coupon-hub] clearUnmetBxgyPromotion', err);
        });
      }
      if (this.bxgyCartAddEnabled) {
        await this.loadBxgySelectableProducts();
      }
      // 🔴 (2026-05-28) Modal 재진입 시 stock add back — 기존 group 의 자식 라인 qty 가 selectableProducts.stock 에서 차감된 값.
      //    confirmBxgy 시점에 cleanupBxgyGroup 으로 삭제되므로 stock 검사에서 reservedByVariant 더해야 정확.
      const ctx = this.state.bxgyContext;
      if (ctx && ctx.existingGroupId) {
        try {
          const cartRes = await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' });
          const cart = await cartRes.json();
          const reservedByVid = {};
          (cart.items || []).forEach((line) => {
            const matchGroup = line.properties?._promotion_group_id === ctx.existingGroupId;
            const isChild = line.properties?._promotion_bxgy_anchor !== 'true';
            if (matchGroup && isChild) {
              reservedByVid[String(line.variant_id)] = (reservedByVid[String(line.variant_id)] || 0) + (line.quantity || 0);
            }
          });
          ctx.reservedByVariant = reservedByVid;
          // selectableProducts 의 stock 보정
          (ctx.selectableProducts || []).forEach((item) => {
            const vid = String(item.variantId || (item.variantGid || '').replace(/.*\//, ''));
            const reserved = reservedByVid[vid] || 0;
            if (reserved > 0 && Number.isFinite(item.stock)) {
              item.stock += reserved;
            }
          });
          this.renderBxgyModal();
        } catch (err) {
          console.warn('[coupon-hub] reserved stock recalc', err);
        }
      }
    }

    // 🔴 closeBxgyModal (2026-05-24) — 디펜시브 dropdown 복원 통합.
    // confirmed=false (cancel / overlay / ESC 등 사용자 미적용 close) → ctx.prevValue 로 dropdown 자동 복원.
    // confirmed=true (confirmBxgy 만) → 복원 skip.
    // ctx 정리도 여기서 한 번에. 호출처는 closeBxgyModal({confirmed}) 만 전달, ctx=null 별도로 안 해도 됨.
    // 회귀 case: cancelBxgy 의 selector 가 data-variant-id 에 lineKey 박아 cell 매칭 실패하던 회귀 fix.
    closeBxgyModal({ confirmed = false } = {}) {
      const ctx = this.state.bxgyContext;
      if (!confirmed && ctx) {
        const cell = ctx.anchorCell || document.querySelector(
          `[data-cart-promo-cell][data-line-key="${CSS.escape(ctx.lineKey || '')}"]`
        );
        if (cell) {
          const select = cell.querySelector('[data-promo-select]');
          if (select) {
            select.value = ctx.prevValue || '';
            this.refreshDropdownInput(select);
          }
        }
      }
      this.state.bxgyContext = null;
      const modal = document.querySelector('[data-bxgy-modal]');
      if (!modal) return;
      modal.hidden = true;
      const scrollContainer = modal.querySelector('.cart-bxgy-modal__inner') || modal;
      if (typeof bodyScroll !== 'undefined') bodyScroll.unlock(scrollContainer);
    }

    // selectable API는 modal open 시 최초 1 page만 호출한다.
    // 이후 page는 사용자가 Load more를 누른 경우에만 가져온다.
    async loadBxgySelectableProducts() {
      const ctx = this.state.bxgyContext;
      if (!ctx) return;
      const discountId = ctx.promotion.id || ctx.promotion.discountId || ctx.promotion.code;
      if (!discountId) return;
      const limit = ctx.catalogLimit || 5;
      // 🔴 솔드아웃 필터 자동 보충 (2026-05-27).
      //   verifyBxgyCandidatesAvailability 로 솔드아웃 제거 시 가시 catalog 수가 catalogLimit 미만이 되면
      //   다음 페이지를 자동으로 추가 fetch → 가시 후보 수 ≥ target 유지.
      //   safety: 최대 5 페이지까지 fetch (catalogLimit×5 = 60 candidate 검증) — 무한루프 방지.
      const target = ctx.renderTarget || limit;
      const MAX_REFILL_PAGES = 5;
      try {
        for (let pageIdx = 0; pageIdx < MAX_REFILL_PAGES; pageIdx++) {
          const params = new URLSearchParams({ discount_id: discountId });
          if (ctx.productId) params.set('product_id', this.toShopifyGid('Product', ctx.productId));
          if (this.shopDomain) params.set('shop', this.shopDomain);
          params.set('limit', String(limit));
          params.set('offset', String(ctx.catalogOffset || 0));
          const url = `${this.apiBase}/promotions/products/selectable?${params.toString()}`;
          const res = await fetch(url, { headers: this.authHeaders(), credentials: 'omit' });
          if (!res.ok) throw new Error(`eligible products ${res.status}`);
          const payload = await res.json();
          const products = payload.selectableProducts || payload.selectable_products || payload.data?.selectableProducts || [];
          const offset = Number(payload.offset ?? ctx.catalogOffset ?? 0);
          const rawProducts = products
            .map((item, index) => this.normalizeBxgySelectableProduct(item, offset + index))
            .filter(Boolean);
          // 🔴 BE 디펜시브 — Storefront API 로 variant.available 재검증 (2026-05-27).
          //   BE response 의 stock 정의가 예약 hold 미반영(stock=N>0 인데 실제 판매가능=0).
          //   사용자가 솔드아웃 candidate 선택 → cart/add 422 → "Cart update failed" 회귀.
          //   /products/{handle}.js 로 variant.available 재확인:
          //     - 200 + available=false → hide (회귀 차단)
          //     - 404 (handle 불일치) → graceful fallback: BE stock 신뢰 (전체 후보 탈락 회피)
          //   handle 단위 grouping + 인스턴스 캐시 (_storefrontProductCache).
          //   근본: misto-msa 의 product cache 정의 정합 (별도 PR 영역).
          //   회귀 case: wiki/docs/bxgy-selectable-sold-out-fe-defense-2026-05-26.md
          const verifiedProducts = await this.verifyBxgyCandidatesAvailability(rawProducts);
          // 🔴 Storefront GraphQL 정량 보정 (2026-05-27). BE catalog stock 과장/stale 디펜시브.
          //   verify (available 필터) → correction (quantityAvailable min) 순. token 없으면 no-op.
          const correctedProducts = await this.applyStorefrontStockCorrection(verifiedProducts);
          // selectable 응답은 이미 cart/add에 필요한 ProductVariant GID를 포함한다.
          ctx.selectableProducts = ctx.catalogOffset > 0
            ? [...ctx.selectableProducts, ...correctedProducts]
            : (pageIdx === 0 ? correctedProducts : [...ctx.selectableProducts, ...correctedProducts]);
          ctx.catalogOffset = offset + products.length;
          // BE 디펜시브: raw 응답이 limit 미만이면 다음 페이지 없는 것으로 확정한다.
          ctx.catalogHasMore = !!payload.hasMore && products.length >= limit;
          // 자동 보충 종료 조건
          if (!ctx.catalogHasMore) break;
          const visibleCount = this.bxgyCatalogVisibleCount(ctx);
          if (visibleCount >= target) break;
        }
      } catch (err) {
        console.warn('[coupon-hub] loadBxgySelectableProducts', err);
        ctx.catalogError = 'Additional items unavailable. Select from cart items.';
      } finally {
        ctx.catalogLoading = false;
        ctx.catalogLoadingMore = false;
        this.renderBxgyModal();
      }
    }

    renderBxgyModal() {
      const ctx = this.state.bxgyContext;
      if (!ctx) return;
      // 🔴 (2026-05-28) Y-only modal — required = applicationCount × getQty (multi-application 자동 max).
      //    anchor qty / buyQty 의 자동 적용 횟수. a × 6 + 2+1 → 3 applications → required = 3.
      // 🔴 (2026-05-28) anchor 는 정확히 그 lineKey 1라인. 같은 variant 다른 라인 합산 금지 (get 과선택 회귀 차단).
      //   회귀 case: wiki/docs/bxgy-unique-line-identity-2026-05-28.md
      const anchorCell = ctx.anchorSelectionId
        ? Array.from(document.querySelectorAll('[data-cart-promo-cell]'))
            .find((c) => this.bxgySelectionId(c) === ctx.anchorSelectionId)
        : null;
      const anchorQty = anchorCell ? this.bxgyLineQuantity(anchorCell) : 0;
      const buyQty = Math.max(1, parseInt(ctx.promotion.buyQty || '1', 10) || 1);
      const getQty = Math.max(1, parseInt(ctx.promotion.getQty || '1', 10) || 1);
      const applicationCount = Math.max(1, Math.floor(anchorQty / buyQty) || 1);
      const required = applicationCount * getQty;
      // 🔴 (2026-05-28) ctx 에 저장 — confirmBxgy 의 bxgyRoleByLine 이 multi-app required 기반 분배.
      ctx.required = required;
      ctx.applicationCount = applicationCount;
      const modal = document.querySelector('[data-bxgy-modal]');
      if (!modal) return;

      const titleEl = modal.querySelector('[data-bxgy-title]');
      // 🔴 (2026-05-28) "free" 제거 — BXGY 정책 (100% off / fixed_amount 등) 따라 무료 아닐 수도. 단순 "Select N item(s)".
      if (titleEl) titleEl.textContent = `${ctx.promotion.name} — Select ${required} item${required > 1 ? 's' : ''}`;
      const labelEl = modal.querySelector('[data-bxgy-selected-label]');
      if (labelEl) labelEl.textContent = `Selected (${ctx.selectedIds.length} / ${required})`;

      // 🔴 (2026-05-28) Anchor 행 — modal 상단 별도 표시 (X = modal 밖). 횟수 표기 X (사용자 결정).
      const anchorRow = modal.querySelector('[data-bxgy-anchor-row]');
      const anchorInfoEl = modal.querySelector('[data-bxgy-anchor-info]');
      const anchorCellForRow = anchorCell;
      if (anchorRow && anchorInfoEl && anchorCellForRow) {
        const aName = anchorCellForRow.dataset.productName || anchorCellForRow.dataset.variantId || '';
        anchorInfoEl.textContent = `${aName} ×${anchorQty}`;
        anchorRow.hidden = false;
      } else if (anchorRow) {
        anchorRow.hidden = true;
      }

      // 🔴 (2026-05-28) Buy 행 아래 혜택 안내 문구 — "선택하면 무료인가?" 혼동 제거. 영문, subtle.
      const benefitEl = modal.querySelector('[data-bxgy-benefit]');
      if (benefitEl) {
        const benefitMsg = this.bxgyBenefitMessage(ctx.promotion, required);
        if (benefitMsg) {
          benefitEl.textContent = benefitMsg;
          benefitEl.hidden = false;
        } else {
          benefitEl.hidden = true;
        }
      }

      // 🔴 (2026-05-28) GY 신규행 only 정책 — cart 라인 후보 제거. Selected = catalog (BE selectableProducts) only.
      const catalogCandidates = (ctx.selectableProducts || [])
        .filter((item) => !this.isBxgyCatalogItemAlreadyRepresented(item, ctx))
        .map((item) => ({
          ...item,
          quantity: ctx.selectedIds.includes(item.id) ? (ctx.addQuantities[item.id] || 1) : 1,
        }));
      const candidates = [...catalogCandidates];
      const lookupById = new Map(candidates.map((c) => [c.id, c]));
      const selectedQuantity = this.bxgySyncSelectedQuantity(ctx, lookupById);
      this.updateBxgyModalStatus(ctx);

      // Available — product card grid (이미지 위 / 이름·가격 아래 / 우상단 ↗)
      // 🔴 [중요] 한도 도달 시: 미선택 카드 .is-disabled (클릭 무시), 선택된 catalog 카드의 + 버튼 disabled.
      const limitReached = selectedQuantity >= required;
      const renderAvailableCard = (c) => {
        const sel = ctx.selectedIds.includes(c.id);
        const disabled = !sel && limitReached;
        const locked = c.id === ctx.anchorSelectionId;
        const inCart = c.source === 'cart';
        const imgHtml = c.image
          ? `<img class="bxgy-card__image" src="${this.escapeHtml(c.image)}" alt="" loading="lazy">`
          : '<span class="bxgy-card__image bxgy-card__image--placeholder" aria-hidden="true"></span>';
        const cartBadgeHtml = inCart
          ? '<span class="bxgy-card__cart-badge" aria-label="In your cart">In your cart</span>'
          : '';
        const linkHtml = c.url
          ? `<a class="bxgy-card__link" href="${this.escapeHtml(c.url)}" target="_blank" rel="noopener" aria-label="View product details" title="View product">↗</a>`
          : '';
        // 🔴 storefront 재고 상한 도달 시 + 버튼 disabled (2026-05-27).
        const stock = Number.isFinite(c.stock) ? c.stock : Infinity;
        const stockReached = c.source === 'catalog' && sel && Number(c.quantity || 0) >= stock;
        const incDisabled = (limitReached || stockReached) ? ' disabled aria-disabled="true"' : '';
        const qtyHtml = c.source === 'catalog' && sel
          ? `<span class="bxgy-card__qty">
              <button type="button" class="bxgy-card__qty-btn" data-bxgy-qty="decrease" data-bxgy-id="${this.escapeHtml(c.id)}" aria-label="Decrease quantity">−</button>
              <span class="bxgy-card__qty-value">x${this.escapeHtml(String(c.quantity))}</span>
              <button type="button" class="bxgy-card__qty-btn" data-bxgy-qty="increase" data-bxgy-id="${this.escapeHtml(c.id)}" aria-label="Increase quantity"${incDisabled}>+</button>
            </span>`
          : '';
        const classes = ['bxgy-card'];
        if (sel) classes.push('is-selected');
        if (disabled) classes.push('is-disabled');
        if (locked) classes.push('is-locked');
        const ariaDisabled = disabled || locked;
        return `<div class="${classes.join(' ')}">
          <button type="button" class="bxgy-card__pick" data-bxgy-pick="${this.escapeHtml(c.id)}" aria-pressed="${sel}" aria-disabled="${ariaDisabled}"${ariaDisabled ? ' tabindex="-1"' : ''}>

            <span class="bxgy-card__media">${imgHtml}${cartBadgeHtml}${sel ? '<span class="bxgy-card__check" aria-hidden="true">✓</span>' : ''}</span>
            <span class="bxgy-card__info">
              <span class="bxgy-card__name">${this.escapeHtml(c.name)}</span>
              ${c.option ? `<span class="bxgy-card__option">${this.escapeHtml(c.option)}</span>` : ''}
              <span class="bxgy-card__price">${this.escapeHtml(this.formatBxgyPrice(c.price))}</span>
            </span>
          </button>
          ${qtyHtml}
          ${linkHtml}
        </div>`;
      };
      // 🔴 grid 정합 + renderTarget cap (2026-05-24).
      // 규칙:
      //   - renderTarget = 한 번에 보일 카드 cap (디폴트 catalogLimit=12). Load more 클릭 시 += catalogLimit.
      //   - BE 더 있음 (catalogHasMore) → cap 안에서 6 배수만 노출 (partial row 숨김) + Load more
      //   - BE 끝남 → cap 안에서 전부 노출 (partial row OK) + hidden 남았으면 Load more 활성
      //   - cart 라인은 절대 안 떨어뜨림 (사용자가 카트에 담은 것)
      // 회귀 case (2026-05-24): autoFill 로 17~19개 누적 + catalogHasMore=false 시 전부 표시되던 회귀 → renderTarget cap 으로 해결
      const COLS_LCM = 6;
      // 🔴 (2026-05-28) cartCandidates 제거됨 (GY 신규행 only). cartCount = 0 으로 고정.
      const cartCount = 0;
      const totalLoaded = candidates.length;
      const renderTarget = ctx.renderTarget || ctx.catalogLimit || COLS_LCM;
      const cap = Math.min(totalLoaded, Math.max(cartCount, renderTarget));
      let renderCount;
      if (ctx.catalogHasMore) {
        const floorMultiple = Math.floor(cap / COLS_LCM) * COLS_LCM;
        renderCount = Math.max(cartCount, floorMultiple);
      } else {
        renderCount = cap;
      }
      const renderedCandidates = candidates.slice(0, renderCount);
      const showLoadMore = ctx.catalogHasMore || renderCount < totalLoaded;

      const availableHtml = renderedCandidates.map(renderAvailableCard).join('');
      const skeletonHtml = ctx.catalogLoading
        ? Array.from({ length: COLS_LCM }, () =>
            '<div class="bxgy-card bxgy-card--skeleton" aria-hidden="true">' +
              '<span class="bxgy-card__media bxgy-card__media--skeleton"></span>' +
              '<span class="bxgy-card__info">' +
                '<span class="bxgy-card__name bxgy-card__name--skeleton"></span>' +
                '<span class="bxgy-card__price bxgy-card__price--skeleton"></span>' +
              '</span>' +
            '</div>'
          ).join('')
        : '';
      const loadMoreHtml = showLoadMore
        ? `<button type="button" class="cart-bxgy-modal__load-more" data-bxgy-load-more ${ctx.catalogLoadingMore ? 'disabled' : ''}>${ctx.catalogLoadingMore ? 'Loading' : 'Load more'}</button>`
        : '';
      modal.querySelector('[data-bxgy-available]').innerHTML =
        (availableHtml + skeletonHtml + loadMoreHtml) ||
        (ctx.catalogLoading
          ? ''
          : '<p class="cart-bxgy-modal__empty">No available items</p>');

      // 🔴 (2026-05-28) Selected — Modal 단순화 결정. 모달 안 선택은 모두 무료이므로 FREE 라벨 X, is-free 클래스 X. 정가만 표시.
      //   실가는 cart 반영 후 Shopify final_price SSOT 가 처리.
      const selectedItems = ctx.selectedIds
        .map((id) => lookupById.get(id))
        .filter(Boolean);

      const renderSelectedRow = (c) => {
        const locked = c.id === ctx.anchorSelectionId;
        const imgHtml = c.image
          ? `<img class="bxgy-row__image" src="${this.escapeHtml(c.image)}" alt="" loading="lazy">`
          : '<span class="bxgy-row__image bxgy-row__image--placeholder" aria-hidden="true"></span>';
        const tag = locked ? 'div' : 'button type="button"';
        const deselectAttr = locked ? 'aria-disabled="true"' : `data-bxgy-deselect="${this.escapeHtml(c.id)}"`;
        const qty = c.quantity || 1;
        const qtyBadge = qty >= 2 ? ` <span class="bxgy-row__qty">x${this.escapeHtml(String(qty))}</span>` : '';
        return `<${tag} class="bxgy-row ${locked ? 'is-locked' : ''}" ${deselectAttr}>
          ${imgHtml}
          <span class="bxgy-row__info">
            <span class="bxgy-row__name">${this.escapeHtml(c.name)}${qtyBadge}</span>
            ${c.option ? `<span class="bxgy-row__option">${this.escapeHtml(c.option)}</span>` : ''}
            <span class="bxgy-row__price">${this.escapeHtml(this.formatBxgyPrice(c.price))}</span>
          </span>
          ${locked ? '' : '<span class="bxgy-row__remove" aria-hidden="true">×</span>'}
        </${locked ? 'div' : 'button'}>`;
      };
      modal.querySelector('[data-bxgy-selected]').innerHTML =
        selectedItems.length === 0
          ? '<p class="cart-bxgy-modal__empty">No items selected — pick from below</p>'
          : selectedItems.map(renderSelectedRow).join('');

      this.updateBxgyModalStatus(ctx);
      if (labelEl) labelEl.textContent = `Selected (${selectedQuantity} / ${required})`;
      const summaryEl = modal.querySelector('[data-bxgy-summary]');
      if (summaryEl) {
        if (ctx.catalogLoading) {
          summaryEl.textContent = 'Loading available items';
        } else if (ctx.catalogError) {
          summaryEl.innerHTML = `<span class="cart-bxgy-modal__warning">${this.escapeHtml(ctx.catalogError)}</span>`;
        } else {
          // 상단 'Selected (N / M)' 라벨과 중복이라 비움. summary 슬롯은 로딩/에러 메시지용으로 유지.
          summaryEl.textContent = '';
        }
      }
      const confirmBtn = modal.querySelector('[data-bxgy-confirm]');
      if (confirmBtn) {
        const disabled = selectedQuantity < required;
        confirmBtn.disabled = disabled;
        confirmBtn.toggleAttribute('disabled', disabled);
        confirmBtn.setAttribute('aria-disabled', String(disabled));
      }
    }

    updateBxgyModalStatus(ctx) {
      if (!ctx) return;
      const modal = document.querySelector('[data-bxgy-modal]');
      if (!modal) return;
      const required = ctx.promotion.buyQty + ctx.promotion.getQty;
      const selectedQuantity = Number(ctx.selectedQuantity || 0);
      const labelEl = modal.querySelector('[data-bxgy-selected-label]');
      if (labelEl) labelEl.textContent = `Selected (${selectedQuantity} / ${required})`;
      const summaryEl = modal.querySelector('[data-bxgy-summary]');
      if (summaryEl) summaryEl.textContent = `${selectedQuantity} / ${required} selected`;
      const confirmBtn = modal.querySelector('[data-bxgy-confirm]');
      if (confirmBtn) {
        const disabled = selectedQuantity < required;
        confirmBtn.disabled = disabled;
        confirmBtn.toggleAttribute('disabled', disabled);
        confirmBtn.setAttribute('aria-disabled', String(disabled));
      }
    }

    // 🔴 [중요] 한도(promotion.buyQty + getQty) 가드 필수. 카드 클릭 = 토글, qty 조정은 +/− 버튼.
    // 회귀 case: docs/superpowers/plans/bxgy-modal-selection-limit.md
    bxgyPick(el) {
      const ctx = this.state.bxgyContext;
      if (!ctx) return;
      const id = el.dataset.bxgyPick;
      if (id === ctx.anchorSelectionId) return;
      if (ctx.selectedIds.includes(id)) {
        ctx.selectedIds = ctx.selectedIds.filter((x) => x !== id);
        delete ctx.addQuantities[id];
      } else {
        const item = this.bxgyCandidateById(id);
        if (!item) return;
        // 🔴 (2026-05-28) max = ctx.required (multi-application 자동 max). bxgyAdjustQuantity 와 동일 정책.
        const max = (typeof ctx.required === 'number' && ctx.required > 0)
          ? ctx.required
          : Math.max(1, parseInt(ctx.promotion.getQty || '1', 10) || 1);
        const selectedQuantity = this.bxgySelectedQuantity(ctx);
        const addQty = item.source === 'catalog' ? 1 : (item.cell ? this.bxgyLineQuantity(item.cell) : 1);
        if (selectedQuantity + addQty > max) return;
        // 🔴 storefront 재고 가드 (2026-05-27) — catalog candidate 만 (cart-source 는 cart 라인 qty 고정).
        const stock = Number.isFinite(item.stock) ? item.stock : Infinity;
        if (addQty > stock) return;
        ctx.selectedIds = Array.from(new Set([...ctx.selectedIds, id]));
        if (item.source === 'catalog') ctx.addQuantities[id] = 1;
      }
      this.bxgySyncSelectedQuantity(ctx);
      this.updateBxgyModalStatus(ctx);
      this.renderBxgyModal();
    }

    bxgyDeselect(el) {
      const ctx = this.state.bxgyContext;
      if (!ctx) return;
      const id = el.dataset.bxgyDeselect;
      if (id === ctx.anchorSelectionId) return;
      ctx.selectedIds = ctx.selectedIds.filter((x) => x !== id);
      delete ctx.addQuantities[id];
      this.bxgySyncSelectedQuantity(ctx);
      this.updateBxgyModalStatus(ctx);
      this.renderBxgyModal();
    }

    bxgyAdjustQuantity(el) {
      const ctx = this.state.bxgyContext;
      if (!ctx) return;
      const id = el.dataset.bxgyId;
      if (id === ctx.anchorSelectionId) return;
      if (!id || !ctx.selectedIds.includes(id)) return;
      const current = ctx.addQuantities[id] || 1;
      const direction = el.dataset.bxgyQty;
      if (direction === 'increase') {
        // 🔴 (2026-05-28) max = ctx.required (renderBxgyModal 이 applicationCount × getQty 로 계산). multi-application 자동 max 반영.
        //    fallback: getQty (single application).
        const max = (typeof ctx.required === 'number' && ctx.required > 0)
          ? ctx.required
          : Math.max(1, parseInt(ctx.promotion.getQty || '1', 10) || 1);
        const selectedQuantity = this.bxgySelectedQuantity(ctx);
        if (selectedQuantity >= max) return;
        // 🔴 storefront 재고 가드 (2026-05-27)
        const item = this.bxgyCandidateById(id);
        const stock = Number.isFinite(item?.stock) ? item.stock : Infinity;
        if (current + 1 > stock) return;
        ctx.addQuantities[id] = current + 1;
      } else {
        const next = current - 1;
        if (next <= 0) {
          ctx.selectedIds = ctx.selectedIds.filter((x) => x !== id);
          delete ctx.addQuantities[id];
        } else {
          ctx.addQuantities[id] = next;
        }
      }
      this.bxgySyncSelectedQuantity(ctx);
      this.updateBxgyModalStatus(ctx);
      this.renderBxgyModal();
    }

    bxgySyncSelectedQuantity(ctx, lookupById = null) {
      const selectedQuantity = this.bxgySelectedQuantity(ctx, lookupById);
      if (ctx) ctx.selectedQuantity = selectedQuantity;
      return selectedQuantity;
    }

    // 🔴 [모듈] 본문은 assets/cart-promo-bxgy.js (Phase 4c)
    bxgySelectedQuantity(ctx, lookupById = null) { return window.CartPromoBxgy.selectedQuantity(ctx, lookupById); }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (Phase 2a, 2026-05-27).
    //   회귀 case: wiki/docs/bxgy-role-by-line-distribution-2026-05-27.md
    bxgyRoleByLine(ctx, selectedCandidates) { return window.CartCouponHubUtils.bxgyRoleByLine(ctx, selectedCandidates); }
    bxgyRoleBySelectionId(ctx, selectedCandidates) { return window.CartCouponHubUtils.bxgyRoleBySelectionId(ctx, selectedCandidates); }

    // 🔴 단계 노출 (2026-05-24):
    //   1) 이미 fetch 됐는데 hidden (renderTarget cap 으로 가려진) 카드가 있으면 → renderTarget += catalogLimit → re-render only
    //   2) hidden 없고 BE 더 있으면 (catalogHasMore) → 다음 페이지 fetch
    async bxgyLoadMore() {
      const ctx = this.state.bxgyContext;
      if (!ctx || ctx.catalogLoadingMore) return;
      const step = ctx.catalogLimit || 6;
      const cartCount = this.bxgyCartCandidateCells(ctx).length;
      const totalLoaded = cartCount + this.bxgyCatalogVisibleCount(ctx);
      const currentTarget = ctx.renderTarget || ctx.catalogLimit || step;
      if (currentTarget < totalLoaded) {
        // hidden 노출
        ctx.renderTarget = currentTarget + step;
        this.renderBxgyModal();
        return;
      }
      if (!ctx.catalogHasMore) return; // 더 없음
      ctx.catalogLoadingMore = true;
      ctx.renderTarget = currentTarget + step;
      this.renderBxgyModal();
      await this.loadBxgySelectableProducts();
    }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (Phase 2a)
    bxgySelectionId(cell) { return window.CartCouponHubUtils.bxgySelectionId(cell); }
    bxgyLineQuantity(cell) { return window.CartCouponHubUtils.bxgyLineQuantity(cell); }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (Phase M)
    bxgyCartQuantity(cells) { return window.CartCouponHubUtils.bxgyCartQuantity(cells); }

    // 🔴 [정책] anchor cell 만 반환 (2026-05-27). GY 항상 catalog 신규 add 정책.
    //   이전엔 anchor + 같은 promo 적용 cart 라인 + BXGY 자격 가진 cart 라인 모두 반환 → 사용자가
    //   cart-source 라인을 GY 로 선택 가능 → cleanup 분기(user 라인 vs catalog-added) 복잡 + 누적 회귀 위험.
    //   anchor 만 cart-source 로 두고 buy role 진입점 역할. 다른 buy/GY 후보는 모두 catalog (selectableProducts).
    //   카트에 BXGY 자격 다른 라인이 있어도 모달엔 안 보임 — 사용자가 그 라인의 dropdown 으로 별도 적용해야 함.
    //   회귀 case: wiki/docs/bxgy-reentry-cleanup-and-cart-remove-button-capture-2026-05-26.md
    // 🔴 [모듈] 본문은 assets/cart-promo-bxgy.js (Phase 4c)
    //   wrapper 가 this.state.bxgyContext 매개변수로 전달 — 모듈은 ctx 받는 순수 함수.
    bxgyCartCandidateCells(ctx) { return window.CartPromoBxgy.cartCandidateCells(ctx); }
    bxgyCandidateById(id) { return window.CartPromoBxgy.candidateById(id, this.state.bxgyContext); }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (Phase 2a)
    bxgySelectedIdsFromDom() { return window.CartCouponHubUtils.bxgySelectedIdsFromDom(); }

    // 🔴 [모듈] 본문은 assets/cart-promo-bxgy.js (Phase 4c)
    bxgyCatalogVisibleCount(ctx) { return window.CartPromoBxgy.catalogVisibleCount(ctx); }
    isBxgyCatalogItemAlreadyRepresented(item, ctx) { return window.CartPromoBxgy.isCatalogItemAlreadyRepresented(item, ctx); }

    async clearUnmetBxgyPromotion(cell) {
      if (!cell) return;
      cell.dataset.appliedPromotionId = '';
      cell.dataset.appliedPromotionType = '';
      cell.dataset.appliedPromotionRole = '';
      const select = cell.querySelector('[data-promo-select]');
      if (select) select.value = '';
      await this.clearLinePromotion(cell);
    }

    async confirmBxgy() {
      const ctx = this.state.bxgyContext;
      if (!ctx || ctx.confirming) return;
      ctx.confirming = true;
      const cell = document.querySelector(
        `[data-cart-promo-cell][data-line-key="${CSS.escape(ctx.lineKey)}"]`
      );
      // 🔴 cart-items 전체 마스킹 — refreshCartSection 중 깨진 layout flash 방지
      const cartItemsEl = document.querySelector('cart-items');
      cartItemsEl?.classList.add('is-cart-loading');
      // 🔴 confirm 버튼 로딩 상태 — 응답 오래 걸리니 사용자에게 진행 중 신호
      const confirmBtn = document.querySelector('[data-bxgy-confirm]');
      const confirmBtnSpan = confirmBtn?.querySelector('.button__text') || confirmBtn;
      const confirmBtnOriginalHtml = confirmBtnSpan?.innerHTML;
      if (confirmBtn && confirmBtnSpan) {
        confirmBtn.disabled = true;
        confirmBtn.setAttribute('aria-busy', 'true');
        confirmBtn.classList.add('is-loading');
        confirmBtnSpan.innerHTML = '<span class="cart-bxgy-modal__btn-spinner" aria-hidden="true"></span>';
      }
      try {
        // 🔴 [중요] 여기서 clearGiftRows 호출 금지 (2026-05-26 회귀). 이전 GY 라인의 [data-cart-promo-cell] 이
        // DOM 에서 사라지면 아래 promotionCellsToClear(DOM 기반) 가 못 잡아 cleanup 누락 → 카트 누적 회귀.
        // 시각적 flash 는 cart-items.is-cart-loading 마스킹이 차단. 옛 GY cleanup 은 applyToCart 의 removePromoCells
        // → clearOrRemoveLinePromotion 경로가 책임 (catalog-added → removeCartLine, user 라인 → properties wipe).
        // 최종 DOM 갱신은 refreshCartSection 이 처리. 회귀 case: wiki/docs/bxgy-reentry-cleanup-and-cart-remove-button-capture-2026-05-26.md
        // GET = 가격 오름차순으로 저렴한 getQty 개
        const domSelectedIds = this.bxgySelectedIdsFromDom();
        const selectedIds = Array.from(new Set([
          ...(ctx.anchorSelectionId ? [ctx.anchorSelectionId] : []),
          ...(domSelectedIds.length ? domSelectedIds : (ctx.selectedIds || [])),
        ]));
        ctx.selectedIds = selectedIds;
        const selectedCandidates = selectedIds
          .map((id) => this.bxgyCandidateById(id))
          .filter(Boolean);
        const selectedQuantity = this.bxgySelectedQuantity(ctx);
        if (selectedQuantity < ctx.promotion.buyQty + ctx.promotion.getQty) {
          this.renderBxgyModal();
          return;
        }
        const roleBySelectionId = this.bxgyRoleBySelectionId(ctx, selectedCandidates);
        // 🔴 (2026-05-28) Y-only modal — role 분배는 catalog only 대상. anchor 포함하면 price sort 영향으로 catalog 가 'buy' 박히는 회귀.
        //   anchor 는 항상 'buy' (modal 밖 자격 책임). selectedCells 의 anchor 별도 setLinePromotion 으로 박힘.
        const roleByLine = this.bxgyRoleByLine(ctx, selectedCandidates.filter((c) => c.source === 'catalog'));
        const selectedCells = selectedCandidates
          .filter((item) => item.source === 'cart')
          .map((item) => item.cell)
          .sort(
            // dataset.linePrice 콤마 처리 — parseMoney 로 strip
            (a, b) => this.parseMoney(a.dataset.linePrice || '0') - this.parseMoney(b.dataset.linePrice || '0')
          );
        // 🔴 (2026-05-28) 한 줄 합치기 정책 — 같은 catalog item 의 모든 라인 같은 role (modal 안 선택 = 다 get).
        //   하나 quantity=totalQty 로 묶음. 이전엔 flatMap 으로 lineIndex 별 quantity:1 풀어서 cart/add.js
        //   가 properties 동일에도 별도 라인 분리 회귀 발생 (2026-05-28 A7 회귀).
        //   role 분배 의도: lineIndex 0 의 role 을 그 catalog item 의 대표 role 로 사용.
        const catalogAddItems = selectedCandidates
          .filter((item) => item.source === 'catalog')
          .map((item) => {
            const totalQty = Math.max(1, parseInt(ctx.addQuantities[item.id] || '1', 10) || 1);
            return {
              ...item,
              quantity: totalQty,
              // 🔴 (2026-05-28) GY 신규행 only — modal 안 catalog 선택은 모두 'get' (사용자 정책). roleByLine 의존 X.
              bxgyRole: 'get',
            };
          });
        // 🔴 [중요] (2026-05-28) anchor split 제거. 이전 정책 (qty=1 강제) 폐기.
        //    사유: BE Function 이 quantity 합산 자격 판정 가능 (line 단위 아님).
        //    회귀 case: wiki/docs/bxgy-anchor-split-qty-1-per-line-2026-05-27.md (폐기 marker)
        //    Anchor 라인 qty 묶음 유지. catalog add 만.
        const addItems = catalogAddItems;
        // 🔴 (2026-05-28) group_id — 한 confirm 흐름의 anchor + 자식 공유 식별자.
        //    회귀 회피: 다른 anchor 의 같은 variant gy 끼리 properties 다름 → Shopify auto-merge 차단.
        // 🔴 (2026-05-28) group_id = UUID — variant 기반 폐기. 같은 variant 다중 그룹/라인 완전 구별.
        //    confirm 1회 = 그룹 1개 = UUID 1개. anchor + 모든 gift 공유. 다른 그룹과 절대 충돌 X.
        //    회귀 case: wiki/docs/bxgy-unique-line-identity-2026-05-28.md
        const bxgyGroupId = this.bxgyGenerateGroupId();
        // 🔴 (2026-05-28) C11 — modal 재진입 confirm 시 기존 group cleanup 후 신규 group 생성.
        //    existingGroupId 는 openBxgyModal 에서 cell.dataset.appliedPromotionGroupId 보관.
        if (ctx.existingGroupId) {
          await this.cleanupBxgyGroup(ctx.existingGroupId, { includeAnchor: false });
          // 🔴 (2026-05-28) anchor lineKey 재해석 — DOM dataset.lineKey 가 live cart 대비 stale 일 수 있음
          //   (타이밍 re-key / SSR stale). 같은 variant 라인이 여러 개면 setLinePromotion 의 variant fallback 이
          //   모호해져 "cart line not found" 로 실패. existingGroupId + anchor flag 로 cart.js 현재 key 재조회 (variant 추측 X).
          //   회귀 case: wiki/docs/bxgy-unique-line-identity-2026-05-28.md
          try {
            const freshCart = await (await fetch(MISTO.localeUrl('/cart.js'), { credentials: 'same-origin' })).json();
            const freshAnchor = (freshCart.items || []).find(
              (l) => l.properties &&
                l.properties._promotion_group_id === ctx.existingGroupId &&
                l.properties._promotion_bxgy_anchor === 'true'
            );
            if (freshAnchor && cell && cell.dataset.lineKey !== freshAnchor.key) {
              cell.dataset.lineKey = freshAnchor.key;
              ctx.lineKey = freshAnchor.key;
              // anchorSelectionId 도 fresh key 로 — 아래 isAnchor 판정(bxgySelectionId === anchorId)이 깨지지 않게.
              ctx.anchorSelectionId = freshAnchor.key;
            }
          } catch (_e) { /* keep DOM key */ }
        }
        const selectedCellSet = new Set(selectedCells);
        // 🔴 [중요] (2026-05-28) existingGroupId 는 cleanupBxgyGroup 이 group_id SSOT 로 이미 정리함.
        //   DOM dataset 기반 removeCells 가 같은 group 의 cell 을 또 잡으면, removeCartLine 이 stale/꼬인
        //   dataset.lineKey 로 같은 variant 의 plain standalone 라인을 오삭제 (회귀).
        //   회귀 case: wiki/docs/bxgy-reselect-removes-standalone-same-variant-2026-05-28.md
        //   → 이미 group cleanup 처리한 cell 은 removeCells 에서 제외. group_id SSOT 우선.
        const inCleanedGroup = (targetCell) =>
          !!ctx.existingGroupId &&
          (targetCell.dataset.appliedPromotionGroupId || '') === ctx.existingGroupId;
        const removeCells = this.promotionCellsToClear(cell, ctx.prevValue).filter(
          (targetCell) => !selectedCellSet.has(targetCell) && !inCleanedGroup(targetCell)
        );
        selectedCells.forEach((targetCell) => {
          const previousPromotionId = targetCell.dataset.appliedPromotionId || '';
          if (!previousPromotionId || previousPromotionId === ctx.promotion.id) return;
          this.promotionCellsToClear(targetCell, previousPromotionId).forEach((previousCell) => {
            if (!selectedCellSet.has(previousCell) && !inCleanedGroup(previousCell) && !removeCells.includes(previousCell)) {
              removeCells.push(previousCell);
            }
          });
        });
        removeCells.forEach((targetCell) => {
          targetCell.dataset.appliedPromotionId = '';
          targetCell.dataset.appliedPromotionType = '';
          targetCell.dataset.appliedPromotionRole = '';
        });
        // 🔴 anchor = openBxgyModal 진입 cell (= ctx.anchorSelectionId 일치). 부모 마킹의 SSOT.
        // 가격 sort 결과 anchor 가 get role 받아도 anchor flag 는 별도. SSR/JS 모두 anchor flag 우선해 부모로 표시.
        const anchorId = ctx.anchorSelectionId;
        selectedCells.forEach((targetCell) => {
          const role = roleBySelectionId.get(this.bxgySelectionId(targetCell)) || 'buy';
          const isAnchor = this.bxgySelectionId(targetCell) === anchorId;
          targetCell.dataset.appliedPromotionId = ctx.promotion.id;
          targetCell.dataset.appliedPromotionType = ctx.promotion.type || '';
          targetCell.dataset.appliedPromotionRole = role;
          if (isAnchor) {
            targetCell.dataset.appliedPromotionAnchor = 'true';
          } else {
            delete targetCell.dataset.appliedPromotionAnchor;
          }
        });

        const applied = await this.applyToCart({
          promoCells: selectedCells.map((targetCell) => {
            const isAnchor = this.bxgySelectionId(targetCell) === anchorId;
            return {
              cell: targetCell,
              // 🔴 (2026-05-28) anchor 는 항상 role='buy' (modal 밖 자격 책임). 자식은 catalog add 라인.
              bxgyRole: isAnchor ? 'buy' : (roleBySelectionId.get(this.bxgySelectionId(targetCell)) || 'buy'),
              bxgyAnchor: isAnchor,
              bxgyGroupId,
            };
          }),
          promo: ctx.promotion,
          removePromoCells: removeCells,
          addItems: addItems.map((item) => ({ ...item, bxgyGroupId })),
          bxgyGroupId,
        });
        if (!applied) {
          ctx.catalogError = 'Cart update failed. Please try again.';
          this.renderBxgyModal();
          return;
        }
        this.closeBxgyModal({ confirmed: true });
        // 🔴 renderAll 중복 호출 제거 (2026-05-24) — applyToCart → refreshCartSection 내부에서 이미 호출됨.
        //    이중 호출 race 가 cart-item--gift 마킹 → swap → 재마킹 순서 어긋남으로 cart-item__actions--mobile 노출 회귀 일으킴.
        //    회귀 case: docs/policy/cart-coupon-hub.md §회귀 함정
      } finally {
        ctx.confirming = false;
        // confirm 버튼 로딩 해제
        if (confirmBtn && confirmBtnSpan) {
          confirmBtn.classList.remove('is-loading');
          confirmBtn.removeAttribute('aria-busy');
          confirmBtn.disabled = false;
          if (confirmBtnOriginalHtml !== undefined) confirmBtnSpan.innerHTML = confirmBtnOriginalHtml;
        }
        // cart 마스킹 해제 — DOM swap + render 다 끝났음 보장
        requestAnimationFrame(() => {
          document.querySelector('cart-items')?.classList.remove('is-cart-loading');
        });
      }
    }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (Phase M)
    clearGiftRows(parentKey) { return window.CartCouponHubUtils.clearGiftRows(parentKey); }

    // POS 패턴: 부모(anchor) 라인 삭제 시 linked gift 까지 cascade 삭제 + confirm
    // 출처: misto-pos `useCart.ts#handleDeleteProduct` (lines 388-412)
    //
    // 🔴 cascade 처리 (2026-05-26): 부모 + 자식 모두 한 번의 /cart/update.js updates 로 제거.
    //   Shopify native remove (X 버튼 link 클릭) 은 부모 라인만 제거 → 자식 GY cart 라인 orphan 남음.
    //   우리가 e.preventDefault() 로 native 차단 후 다 처리.
    // 🔴 [모듈] 본문은 assets/cart-promo-cascade.js (Phase J, 2026-05-27)
    handleParentRemoveCascade(e, removeBtn) { return window.CartPromoCascade.handleParentRemove(e, removeBtn, this); }
    removeParentCascade(ctx) { return window.CartPromoCascade.parentCascade(ctx, this); }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js
    escapeHtml(str) { return window.CartCouponHubUtils.escapeHtml(str); }

    // 🔴 cancelBxgy (2026-05-24) — dropdown 복원 + ctx 정리는 closeBxgyModal 내부에서 일괄 처리.
    cancelBxgy() {
      this.closeBxgyModal();
    }

    // ---- Apply to Shopify cart -----------------------------------------

    async applyToCart(change) {
      this.classList.add('is-loading');
      try {
        if (change.removeCoupon) {
          await this.applyDiscountCode('');
        } else if (change.applyCoupon) {
          await this.applyDiscountCode(change.applyCoupon.id);
        }
        if (change.removePromoCell) {
          await this.clearOrRemoveLinePromotion(change.removePromoCell);
        } else if (change.removePromoCells) {
          for (const removeCell of change.removePromoCells) {
            await this.clearOrRemoveLinePromotion(removeCell);
          }
        }
        if (change.promoCell) {
          await this.setLinePromotion(change.promoCell, change.promo);
        }
        if (change.promoCells && change.promo) {
          for (const promoCell of change.promoCells) {
            const targetCell = promoCell?.cell || promoCell;
            await this.setLinePromotion(
              targetCell,
              change.promo,
              promoCell?.bxgyRole,
              !!promoCell?.bxgyAnchor,
              undefined,
              promoCell?.bxgyGroupId || change.bxgyGroupId || '',
            );
          }
        }
        if (change.addItems && change.addItems.length && change.promo) {
          await this.addPromotedCartItems(change.addItems, change.promo, change.bxgyGroupId || '');
        }
        await this.refreshCartSection();
        return true;
      } catch (err) {
        console.error('[coupon-hub] applyToCart failed', err);
        return false;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    // 🔴 [모듈] 본문은 assets/cart-promo-effects.js (Phase 3, 2026-05-27)
    applyDiscountCode(code) { return window.CartPromoEffects.applyDiscountCode(code); }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (Phase I)
    promotionCellsToClear(sourceCell, promotionId) { return window.CartCouponHubUtils.promotionCellsToClear(sourceCell, promotionId); }

    // 🔴 [모듈] 본문은 assets/cart-coupon-utils.js (promotion code / properties 생성)
    promotionCodeForCart(promo) { return window.CartCouponHubUtils.promotionCodeForCart(promo); }
    promotionProperties(promo, bxgyRole = '', anchor = false, addedByBxgy = false, groupId = '') {
      return window.CartCouponHubUtils.promotionProperties(promo, bxgyRole, anchor, addedByBxgy, groupId);
    }

    async setLinePromotion(cell, promo, bxgyRole = '', anchor = false, forceQuantity, groupId = '') {
      const code = this.promotionCodeForCart(promo);
      if (!cell || !code) return;
      await this.changeLineProperties(cell.dataset.lineKey, (properties) => {
        const next = { ...properties };
        delete next.promotion_code;
        delete next.promotion_id;
        delete next.promotion_title;
        delete next.promotion_type;
        delete next.promotion_bxgy_role;
        delete next.promotion_bxgy_anchor;
        delete next._promotion_bxgy_role;
        delete next._promotion_bxgy_anchor;
        delete next._promotion_group_id;
        Object.assign(next, this.promotionProperties(promo, bxgyRole, anchor, false, groupId));
        return next;
      }, cell.dataset.variantId, forceQuantity);
    }

    // 🔴 [모듈] 본문은 assets/cart-promo-effects.js (Phase 3)
    addPromotedCartItems(items, promo, groupId = '') { return window.CartPromoEffects.addPromotedCartItems(items, promo, groupId); }

    // 🔴 [모듈] 본문은 assets/cart-promo-effects.js (2026-05-28) — BXGY group cleanup
    cleanupBxgyGroup(groupKey, options) { return window.CartPromoEffects.cleanupBxgyGroup(groupKey, options); }

    // 🔴 분기 (2026-05-26):
    //   BXGY 카탈로그에서 add 된 라인(_promotion_added_by_bxgy='true') → cart 라인 자체 제거.
    //   사용자가 원래 카트에 담은 라인 → properties 만 clear (라인 보존, 정가 회복).
    //
    // 판정 SSOT 우선순위:
    //   (1) cell.dataset.appliedPromotionAddedByBxgy === 'true' (SSR 박힘, 빠름)
    //   (2) cart.js line.properties._promotion_added_by_bxgy === 'true' (SSOT, dataset stale 대비 fallback)
    //
    // 🔴 dataset 만 의존하면 안 되는 이유 (2026-05-26 회귀):
    //   재진입 confirmBxgy 흐름에서 dataset 이 mid-flight 으로 cleared 되는 case 가 관찰됨.
    //   cart.js 의 properties 가 actual cart 상태의 SSOT 이므로 fallback 필수.
    // 🔴 [모듈] 본문은 assets/cart-promo-effects.js (Phase 3)
    clearOrRemoveLinePromotion(cell) { return window.CartPromoEffects.clearOrRemoveLinePromotion(cell); }

    // 🔴 /cart/update.js updates qty=0 — SOLD-OUT 함정 회피 (cart-stock-guard 와 동일 패턴).
    //   /cart/change.js 는 솔드아웃 라인에서 422. updates 는 status 200 으로 라인 제거.
    //   회귀 case: wiki/docs/cart-stock-guard-update-vs-change-2026-05-26.md
    // 🔴 [모듈] 본문은 assets/cart-promo-effects.js (Phase 3)
    removeCartLine(cell) { return window.CartPromoEffects.removeCartLine(cell); }

    // 🔴 [모듈] 본문은 assets/cart-promo-effects.js (Phase 3)
    clearLinePromotion(cell) { return window.CartPromoEffects.clearLinePromotion(cell); }

    // 🔴 properties 변경 시 Shopify line key 가 바뀜 (key = variant_id + properties hash).
    // 한 confirm 흐름에서 동일 cart 라인의 setLinePromotion 이 여러 번 호출되면 stale lineKey 로 'cart line not found' throw.
    // variantId 가 있으면 fallback 매칭 + 최신 key 사용.
    // 🔴 quantity 명시 전달 (2026-05-24) — Shopify cart/change.js 가 properties-only body 일 때
    //    qty 가 1 로 reset 되는 회귀 (사용자 cart 라인 qty=N → promo 적용 후 qty=1). item.quantity 그대로 전달해 보존.
    //    회귀 case: docs/policy/cart-coupon-hub.md §회귀 함정 (promo 적용 qty=1 강제)
    // 🔴 quantityOverride (2026-05-27) — BXGY anchor split 시 원본 라인을 qty=1 로 강제 축소.
    //    회귀 case: wiki/docs/bxgy-anchor-split-qty-1-per-line-2026-05-27.md
    // 🔴 [모듈] 본문은 assets/cart-promo-effects.js (Phase 3)
    changeLineProperties(lineKey, buildProperties, variantId, quantityOverride) {
      return window.CartPromoEffects.changeLineProperties(lineKey, buildProperties, variantId, quantityOverride);
    }

    async updateCartAttributes(attributes) {
      try {
        await fetch(MISTO.localeUrl('/cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        });
      } catch (err) {
        console.warn('[coupon-hub] updateCartAttributes', err);
      }
    }

    // 🔴 [모듈] 본문은 assets/cart-promo-cascade.js (Phase J)
    openCartConfirm(opts) { return window.CartPromoCascade.openConfirm(opts); }
    closeCartConfirm(opts) { return window.CartPromoCascade.closeConfirm(opts, this); }
    handleCartConfirmOk() { return window.CartPromoCascade.handleConfirmOk(this); }

    // 🔴 [모듈] 본문은 assets/cart-promo-cascade.js (Phase J)
    removeBxgyPromo(ctx) { return window.CartPromoCascade.removeBxgyPromo(ctx, this); }
    clearAllCart() { return window.CartPromoCascade.clearAllCart(this); }

    // 🔴 [모듈] 본문은 assets/cart-promo-sync.js (Phase K)
    refreshCartSection() { return window.CartPromoSync.refreshCartSection(this); }

    // ---- Edge cases (TC-S04 EDGE) --------------------------------------

    async revalidateAll() {
      const cells = Array.from(document.querySelectorAll('[data-cart-promo-cell]'));

      // BOGO 미충족 — 카트 총 qty < buyQty + getQty 인 경우 자동 해제
      for (const cell of cells) {
        const appliedId = cell.dataset.appliedPromotionId;
        if (!appliedId) continue;
        const promos =
          this.state.promotions[cell.dataset.variantId] ||
          this.state.promotions[cell.dataset.productId] ||
          [];
        const promo = promos.find((p) => p.id === appliedId);
        if (!promo) continue;
        if (promo.type === 'buy_x_get_y') {
          const required = promo.buyQty + promo.getQty;
          if (this.getCartTotalQty() < required) {
            const removeCells = this.promotionCellsToClear(cell, appliedId);
            removeCells.forEach((targetCell) => {
              targetCell.dataset.appliedPromotionId = '';
              targetCell.dataset.appliedPromotionType = '';
              targetCell.dataset.appliedPromotionRole = '';
            });
            await this.applyToCart({ removePromoCells: removeCells });
            this.toast(`BOGO unmet — ${promo.name} removed`);
          }
        }
      }

      // 활성 쿠폰 minAmount 미달 시 자동 해제
      if (this.state.activeCouponId) {
        const eligibility = this.computeCouponEligibility();
        if (eligibility.belowMin) {
          const removed = this.state.coupons.find((c) => c.id === this.state.activeCouponId);
          this.state.activeCouponId = null;
          await this.applyToCart({ removeCoupon: true });
          this.toast(`Coupon ${removed ? removed.name : ''} removed (condition unmet)`);
        }
      }

      this.renderAll();
    }

    toast(msg) {
      const el = document.createElement('div');
      el.className = 'cart-coupon-toast';
      el.textContent = msg;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 3000);
    }
  }

  customElements.define('coupon-hub', CouponHub);
})();
